/**
 * Grocery List React Query Hooks
 *
 * With Better Auth, session cookies are sent automatically.
 * No token passing needed - just call the API functions directly.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import * as groceryListApi from "../api/grocery-lists";
import type { GroceryGroupBy } from "../api/grocery-lists";
import { parseCheckoffCausalTransportError } from "../api/grocery-lists";
import { track } from "../analytics";
import {
  completionAfterConfirmedCheckoff,
  isGroceryList,
} from "../grocery-checkoff-analytics";
import {
  compareCheckoffOperationFreshness,
  coordinateCheckoff,
  isCheckoffOperationRetiredAtBaseline,
  loadAdmittedCheckoffRetirementV3Maxima,
  loadCheckoffRetirementMaximaWithHealth,
  loadCheckoffRetirementV3MaximaWithHealth,
  loadOutboxSourcesWithHealth,
  parseCheckoffOperationId,
  purgeConfirmedCheckoffTarget,
  purgeConfirmedCheckoffTargetState,
  purgeDeletedCheckoffTargetState,
  retireCheckoffOperationsThrough,
  isRetryableCheckoffError,
  type CoordinatedCheckoff,
} from "../grocery-outbox";
import {
  createPolicySafeAbsentCheckoffOperationId,
  createPolicySafeCheckoffOperationId,
  createPolicySafeCheckoffOperationIdForTargets,
  incrementCanonicalDecimal,
  resetCheckoffOperationProcessClock,
} from "../grocery-checkoff-operation";
import {
  compareCheckoffEpochs,
  confirmServerCheckoff,
  decodeAuthoritativeGroceryCheckoffSnapshot,
  getConfirmedServerCheckoff,
  getConfirmedServerCheckoffEvidence,
  isCanonicalCheckoffEpoch,
  isServerConfirmedCheckoff,
} from "../grocery-checkoff-confirmed";
import {
  clearExactPolicyRecoveryMarker,
  clearExactProcessPolicyRecoveryMarker,
  mergeCausalGroceryProjection,
  loadProcessPolicyRecoveryMarker,
  readPolicyRecoveryMarker,
  savePolicyRecoveryMarker,
  type CheckoffPolicyRecoveryMarkerV1,
  CHECKOFF_POLICY_CEILING,
  type CausalGroceryProjection,
  type LocalCausalRevision,
} from "../grocery-checkoff-recovery";
import {
  issueRetryableDirectCheckoffError,
  isRetryableDirectCheckoffError,
} from "../grocery-checkoff-retry";
export {
  isRetryableDirectCheckoffError,
} from "../grocery-checkoff-retry";
export type { RetryableDirectCheckoffError } from "../grocery-checkoff-retry";
import type {
  GroceryList,
  GroceryItem,
  GroceryItemToggleResult,
  MealPlan,
  AddGroceryItemRequest,
  AddGroceryItemRequestWithCausality,
  DismissSavingsSuggestionRequest,
  GroceryItemCausalMutationResult,
  ClearCheckedPolicyRejectionSummary,
  ResetGroceryListResponse,
} from "../api/types";
import { GROCERY_LISTS_QUERY_KEY, MEAL_PLANS_QUERY_KEY } from "./query-keys";

export type { GroceryGroupBy };

export { GROCERY_LISTS_QUERY_KEY };

type CausalItem = Pick<
  GroceryItem,
  "id" | "checked" | "checkoffEpoch" | "checkoffOperationId"
>;

function isValidServerTimeMs(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0
  );
}

function maxServerTimeMs(
  ...values: Array<number | undefined>
): number | undefined {
  const valid = values.filter(isValidServerTimeMs);
  return valid.length > 0 ? Math.max(...valid) : undefined;
}

/** Keep read-cache clock metadata monotonic without retaining invalid input. */
function mergeServerTime<T extends { serverTimeMs?: number }>(
  current: T | undefined,
  incoming: T,
): T {
  const merged = { ...(current ?? {}), ...incoming };
  const serverTimeMs = maxServerTimeMs(
    current?.serverTimeMs,
    incoming.serverTimeMs,
  );
  if (serverTimeMs === undefined) {
    delete merged.serverTimeMs;
  } else {
    merged.serverTimeMs = serverTimeMs;
  }
  return merged as T;
}

const localRevisions = new Map<string, LocalCausalRevision>();

/** Test-only lifecycle reset; production callers never need to clear this registry. */
export function resetLocalRevisionsForTests(): void {
  localRevisions.clear();
}

function localRevisionKey(groceryListId: string, itemId: string): string {
  return JSON.stringify([groceryListId, itemId]);
}

function localRevisionFor(
  groceryListId: string,
  itemId: string,
): LocalCausalRevision | undefined {
  return localRevisions.get(localRevisionKey(groceryListId, itemId));
}

function registerLocalRevision(
  groceryListId: string,
  itemId: string,
  checked: boolean,
  operationId: string | undefined,
): LocalCausalRevision | undefined {
  const baseline = getConfirmedServerCheckoff(groceryListId, itemId);
  if (!baseline) return undefined;
  const key = localRevisionKey(groceryListId, itemId);
  const previous = localRevisions.get(key);
  const revision = {
    revision: (previous?.revision ?? 0) + 1,
    checked,
    checkoffEpoch: baseline.checkoffEpoch,
    checkoffOperationId:
      parseCheckoffOperationId(operationId)?.version === 3
        ? operationId!
        : null,
  };
  localRevisions.set(key, revision);
  return revision;
}

function clearLocalRevisionIfCurrent(
  groceryListId: string,
  itemId: string,
  revision: number,
): void {
  const key = localRevisionKey(groceryListId, itemId);
  if (localRevisions.get(key)?.revision === revision)
    localRevisions.delete(key);
}

function causalProjection(
  item: CausalItem,
): CausalGroceryProjection | undefined {
  const value = {
    checkoffEpoch: item.checkoffEpoch,
    checkoffOperationId: item.checkoffOperationId ?? null,
    checked: item.checked,
  };
  return isServerConfirmedCheckoff(value)
    ? { id: item.id, ...value }
    : undefined;
}

function mergeAuthoritativeItem<T extends CausalItem>(
  current: T | undefined,
  incoming: T,
  localRevision?: LocalCausalRevision,
  onObservedHigherEpoch?: (value: CausalGroceryProjection) => void,
): T {
  const incomingProjection = causalProjection(incoming);
  if (!incomingProjection) {
    if (!current) return incoming;
    return {
      ...current,
      ...incoming,
      checked: current.checked,
      checkoffEpoch: current.checkoffEpoch,
      checkoffOperationId: current.checkoffOperationId,
    };
  }

  const currentProjection = current ? causalProjection(current) : undefined;
  if (!currentProjection) {
    return {
      ...(current ?? {}),
      ...incoming,
      checkoffEpoch: incomingProjection.checkoffEpoch,
      checkoffOperationId: incomingProjection.checkoffOperationId,
      checked: incomingProjection.checked,
    } as T;
  }

  const merged = mergeCausalGroceryProjection({
    current: currentProjection,
    incoming: incomingProjection,
    localRevision,
  });
  if (merged.observedHigherEpoch) onObservedHigherEpoch?.(merged.value);
  return {
    ...(current ?? {}),
    ...incoming,
    checked: merged.value.checked,
    checkoffEpoch: merged.value.checkoffEpoch,
    checkoffOperationId: merged.value.checkoffOperationId,
  } as T;
}

/** Compare the causal tuple without treating the checked bit as authority. */
function compareCausalItems(
  left: Pick<CausalItem, "checkoffEpoch" | "checkoffOperationId">,
  right: Pick<CausalItem, "checkoffEpoch" | "checkoffOperationId">,
): number {
  if (left.checkoffEpoch === undefined || right.checkoffEpoch === undefined) {
    return 0;
  }
  const epochOrder = compareCheckoffEpochs(
    left.checkoffEpoch,
    right.checkoffEpoch,
  );
  if (epochOrder !== 0) return epochOrder;
  if (left.checkoffOperationId === right.checkoffOperationId) return 0;
  if (left.checkoffOperationId === null) return -1;
  if (right.checkoffOperationId === null) return 1;
  const leftOperation = parseCheckoffOperationId(left.checkoffOperationId);
  const rightOperation = parseCheckoffOperationId(right.checkoffOperationId);
  if (!leftOperation || !rightOperation) return 0;
  return compareCheckoffOperationFreshness(leftOperation, rightOperation);
}

function mergeItemAtConfirmedBaseline<T extends CausalItem>(
  groceryListId: string,
  current: T | undefined,
  incoming: T,
): T {
  const baseline = getConfirmedServerCheckoff(groceryListId, incoming.id);
  const localRevision = localRevisionFor(groceryListId, incoming.id);
  const clearIfHigher = (value: CausalGroceryProjection) => {
    if (
      localRevision &&
      compareCheckoffEpochs(value.checkoffEpoch, localRevision.checkoffEpoch) >
        0
    ) {
      clearLocalRevisionIfCurrent(
        groceryListId,
        incoming.id,
        localRevision.revision,
      );
    }
  };
  if (!baseline) {
    return mergeAuthoritativeItem(
      current,
      incoming,
      localRevision,
      clearIfHigher,
    );
  }

  const currentProjection = current ? causalProjection(current) : undefined;
  const selected = currentProjection
    ? mergeCausalGroceryProjection({
        current: { id: incoming.id, ...baseline },
        incoming: currentProjection,
      }).value
    : { id: incoming.id, ...baseline };
  const guardedCurrent = {
    ...(current ?? incoming),
    checked: selected.checked,
    checkoffEpoch: selected.checkoffEpoch,
    checkoffOperationId: selected.checkoffOperationId,
  } as T;
  return mergeAuthoritativeItem(
    guardedCurrent,
    incoming,
    localRevision,
    clearIfHigher,
  );
}

function promoteAuthoritativeItem(
  groceryListId: string,
  item: CausalItem,
  serverTimeMs?: number,
): void {
  const incoming = causalProjection(item);
  if (!incoming) return;

  const evidence = getConfirmedServerCheckoffEvidence(groceryListId, item.id);
  const baseline = evidence?.baseline;
  const causalOrder = baseline ? compareCausalItems(incoming, baseline) : 1;
  const selected = baseline
    ? mergeCausalGroceryProjection({
        current: { id: item.id, ...baseline },
        incoming,
      }).value
    : incoming;
  // An out-of-order lower response cannot refresh confirmation provenance for
  // a higher in-memory baseline that it did not itself prove.
  if (selected !== incoming) return;

  // Server-clock evidence is monotonic even when two responses race. A newer
  // causal tuple may still advance the baseline, but it must retain the
  // higher clock sample; an equal/lower tuple is wholly ignored.
  const incomingServerTimeMs = maxServerTimeMs(
    serverTimeMs,
    evidence?.serverTimeMs,
  );
  if (
    isValidServerTimeMs(serverTimeMs) &&
    isValidServerTimeMs(evidence?.serverTimeMs) &&
    serverTimeMs < evidence.serverTimeMs &&
    causalOrder <= 0
  ) {
    return;
  }
  confirmServerCheckoff(groceryListId, item.id, {
    checked: selected.checked,
    checkoffEpoch: selected.checkoffEpoch,
    checkoffOperationId: selected.checkoffOperationId,
  }, incomingServerTimeMs);
}

function cachedCausalEpoch(
  queryClient: QueryClient,
  groceryListId: string,
  itemId: string,
): string | undefined {
  let epoch = getConfirmedServerCheckoff(groceryListId, itemId)?.checkoffEpoch;
  const consider = (item: CausalItem) => {
    const projection = causalProjection(item);
    if (
      projection &&
      (!epoch || compareCheckoffEpochs(projection.checkoffEpoch, epoch) > 0)
    ) {
      epoch = projection.checkoffEpoch;
    }
  };
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
    .forEach((query) => {
      const list = query.state.data;
      if (!isGroceryList(list) || list.id !== groceryListId) return;
      list.items.filter((item) => item.id === itemId).forEach(consider);
    });
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const value = query.state.data;
      const plans: MealPlan[] = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];
      plans.forEach((plan) => {
        if (plan.groceryList?.id !== groceryListId) return;
        plan.groceryList.items
          .filter((item) => item.id === itemId)
          .forEach(consider);
      });
    });
  return epoch;
}

function prepareAndPromoteAuthoritativeItem(
  queryClient: QueryClient,
  groceryListId: string,
  item: CausalItem,
  serverTimeMs?: number,
): void {
  const priorEvidence = getConfirmedServerCheckoffEvidence(
    groceryListId,
    item.id,
  );
  const incomingProjection = causalProjection(item);
  const previousEpoch = cachedCausalEpoch(queryClient, groceryListId, item.id);
  if (
    incomingProjection &&
    previousEpoch &&
    compareCheckoffEpochs(incomingProjection.checkoffEpoch, previousEpoch) > 0
  ) {
    purgeConfirmedCheckoffTarget(
      groceryListId,
      item.id,
      incomingProjection.checkoffEpoch,
    );
    const localRevision = localRevisionFor(groceryListId, item.id);
    if (localRevision) {
      clearLocalRevisionIfCurrent(
        groceryListId,
        item.id,
        localRevision.revision,
      );
    }
  }
  // Mutation responses without a valid server clock can improve the visible
  // epoch-ordered projection, but cannot establish or replace authority.
  if (!isValidServerTimeMs(serverTimeMs)) return;
  promoteAuthoritativeItem(
    groceryListId,
    item,
    maxServerTimeMs(serverTimeMs, priorEvidence?.serverTimeMs),
  );
}

function mergeGroceryList(
  current: GroceryList | undefined,
  incoming: GroceryList,
): GroceryList {
  const currentById = new Map(current?.items.map((item) => [item.id, item]));
  return mergeServerTime(current, {
    ...(current ?? {}),
    ...incoming,
    items: incoming.items.map((item) =>
      mergeItemAtConfirmedBaseline(incoming.id, currentById.get(item.id), item),
    ),
  });
}

/**
 * Reconcile only causal state into a sibling grouping projection. The
 * authoritative response may have a different item membership or grouping,
 * so preserving the sibling's structure prevents an aisle response from
 * temporarily masquerading as a recipe-grouped response. Structural refresh
 * remains the responsibility of the awaited invalidation.
 */
function mergeGroceryListCausalFields(
  current: GroceryList,
  incoming: GroceryList,
): GroceryList {
  const incomingById = new Map(incoming.items.map((item) => [item.id, item]));
  return mergeServerTime(current, {
    ...current,
    serverTimeMs: incoming.serverTimeMs,
    items: current.items.map((item) => {
      const authoritativeItem = incomingById.get(item.id);
      if (!authoritativeItem) return item;

      // Reuse the normal baseline/revision selection and epoch cleanup, but
      // copy only its causal result. The sibling projection owns its item
      // membership and product fields until its structural refetch settles.
      const merged = mergeItemAtConfirmedBaseline(
        incoming.id,
        item,
        authoritativeItem,
      );
      return {
        ...item,
        checked: merged.checked,
        checkoffEpoch: merged.checkoffEpoch,
        checkoffOperationId: merged.checkoffOperationId,
      };
    }),
  });
}

function mergeMealPlanGroceryList(
  current: MealPlan,
  incoming: GroceryList,
): MealPlan {
  const groceryList = current.groceryList;
  if (current.id !== incoming.mealPlanId) {
    return current;
  }
  const currentList = groceryList?.id === incoming.id ? groceryList : undefined;
  const currentById = new Map(
    currentList?.items.map((item) => [item.id, item]),
  );
  return {
    ...current,
    // GET /grocery-lists/:mealPlanId is the authoritative source for the
    // full list, but meal-plan caches intentionally carry only this narrow
    // projection. Hydrate a missing projection only for the matching plan;
    // never copy ingredient, quantity, cost, or other full-list fields here.
    groceryList: mergeServerTime(currentList, {
      id: incoming.id,
      serverTimeMs: incoming.serverTimeMs,
      items: incoming.items.map((item) => {
        const merged = mergeItemAtConfirmedBaseline(
          incoming.id,
          currentById.get(item.id),
          item,
        );
        return {
          id: merged.id,
          checked: merged.checked,
          ...(merged.checkoffEpoch !== undefined
            ? { checkoffEpoch: merged.checkoffEpoch }
            : {}),
          ...(merged.checkoffOperationId !== undefined
            ? { checkoffOperationId: merged.checkoffOperationId }
            : {}),
        };
      }),
    }),
  };
}

function mergeMealPlanProjection(
  current: MealPlan | undefined,
  incoming: MealPlan,
): MealPlan {
  if (!incoming.groceryList) return incoming;
  const currentList = current?.groceryList;
  const currentById =
    currentList && currentList.id === incoming.groceryList.id
      ? new Map(currentList.items.map((item) => [item.id, item]))
      : new Map();
  const metadataSource =
    currentList && currentList.id === incoming.groceryList.id
      ? currentList
      : undefined;
  return {
    ...incoming,
    groceryList: mergeServerTime(metadataSource, {
      ...incoming.groceryList,
      items: incoming.groceryList.items.map((item) =>
        mergeItemAtConfirmedBaseline(
          incoming.groceryList!.id,
          currentById.get(item.id),
          item,
        ),
      ),
    }),
  };
}

/** Promote an authoritative grocery GET and reconcile every cached projection synchronously. */
export function reconcileAuthoritativeGroceryList(
  queryClient: QueryClient,
  incoming: GroceryList,
  targetQueryKey?: readonly unknown[],
): GroceryList {
  const snapshot = decodeAuthoritativeGroceryCheckoffSnapshot(incoming);
  if (snapshot?.id === incoming.id) {
    // A complete authoritative list may replace membership after a rebuild,
    // clear or refresh. Older direct manual reads must not restore those rows.
    beginManualAcquisition(queryClient, incoming.mealPlanId);
    snapshot.items.forEach((item) =>
      prepareAndPromoteAuthoritativeItem(
        queryClient,
        incoming.id,
        item,
        snapshot.serverTimeMs,
      ),
    );
  }

  let mergedForIncomingQuery: GroceryList | undefined;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      const isTarget =
        targetQueryKey &&
        query.queryKey.length === targetQueryKey.length &&
        query.queryKey.every((part, index) => part === targetQueryKey[index]);
      if (isTarget) {
        mergedForIncomingQuery = mergeGroceryList(
          isGroceryList(current) ? current : undefined,
          incoming,
        );
        if (isGroceryList(current)) {
          queryClient.setQueryData(query.queryKey, mergedForIncomingQuery);
        }
        return;
      }
      if (!isGroceryList(current) || current.id !== incoming.id) return;
      const merged = targetQueryKey
        ? mergeGroceryListCausalFields(current, incoming)
        : mergeGroceryList(current, incoming);
      queryClient.setQueryData(query.queryKey, merged);
      if (
        !targetQueryKey &&
        query.queryKey[1] === "meal-plan" &&
        query.queryKey[2] === incoming.mealPlanId
      ) {
        mergedForIncomingQuery = merged;
      }
    });

  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (Array.isArray(current)) {
        queryClient.setQueryData(
          query.queryKey,
          current.map((plan) => mergeMealPlanGroceryList(plan, incoming)),
        );
      } else if (current && typeof current === "object") {
        queryClient.setQueryData(
          query.queryKey,
          mergeMealPlanGroceryList(current as MealPlan, incoming),
        );
      }
    });

  return mergedForIncomingQuery ?? mergeGroceryList(undefined, incoming);
}

/** Promote meal-plan GET projections and keep grocery-list caches at the same causal boundary. */
export function reconcileAuthoritativeMealPlans(
  queryClient: QueryClient,
  incoming: MealPlan[],
): MealPlan[] {
  incoming.forEach((plan) => {
    const groceryList = plan.groceryList;
    const snapshot = groceryList
      ? decodeAuthoritativeGroceryCheckoffSnapshot(groceryList)
      : undefined;
    if (snapshot && groceryList && snapshot.id === groceryList.id) {
      snapshot.items.forEach((item) =>
        prepareAndPromoteAuthoritativeItem(
          queryClient,
          groceryList.id,
          item,
          snapshot.serverTimeMs,
        ),
      );
    }
  });

  const incomingById = new Map(incoming.map((plan) => [plan.id, plan]));
  let result = incoming;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (Array.isArray(current)) {
        const currentById = new Map(current.map((plan) => [plan.id, plan]));
        const merged = incoming.map((plan) =>
          mergeMealPlanProjection(currentById.get(plan.id), plan),
        );
        queryClient.setQueryData(query.queryKey, merged);
        if (query.queryKey.length === 1) result = merged;
      } else if (current && typeof current === "object") {
        const plan = current as MealPlan;
        const next = incomingById.get(plan.id);
        if (next) {
          queryClient.setQueryData(
            query.queryKey,
            mergeMealPlanProjection(plan, next),
          );
        }
      }
    });
  incoming.forEach((plan) =>
    reconcileExistingGroceryProjectionsForPlan(queryClient, plan),
  );
  return result;
}

function mergeGroceryListFromMealPlan(
  current: GroceryList | undefined,
  plan: MealPlan,
): GroceryList | undefined {
  if (!plan.groceryList) return current;
  const source = plan.groceryList;
  if (!current) return undefined;
  return mergeServerTime(current, {
    ...current,
    serverTimeMs: source.serverTimeMs,
    items: current.items.map((item) => {
      const incoming = source.items.find(
        (candidate) => candidate.id === item.id,
      );
      return incoming
        ? mergeItemAtConfirmedBaseline(plan.groceryList!.id, item, {
            ...item,
            ...incoming,
          })
        : item;
    }),
  });
}

function reconcileExistingGroceryProjectionsForPlan(
  queryClient: QueryClient,
  plan: MealPlan,
): void {
  const listId = plan.groceryList?.id;
  if (!listId) return;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (
        !isGroceryList(current) ||
        (current.id !== listId && current.mealPlanId !== plan.id)
      ) {
        return;
      }
      const merged = mergeGroceryListFromMealPlan(current, plan);
      if (merged) queryClient.setQueryData(query.queryKey, merged);
    });
}

export function reconcileAuthoritativeMealPlan(
  queryClient: QueryClient,
  incoming: MealPlan,
): MealPlan {
  const groceryList = incoming.groceryList;
  const snapshot = groceryList
    ? decodeAuthoritativeGroceryCheckoffSnapshot(groceryList)
    : undefined;
  if (snapshot && groceryList && snapshot.id === groceryList.id) {
    snapshot.items.forEach((item) =>
      prepareAndPromoteAuthoritativeItem(
        queryClient,
        groceryList.id,
        item,
        snapshot.serverTimeMs,
      ),
    );
  }
  const current = queryClient.getQueryData<MealPlan>([
    MEAL_PLANS_QUERY_KEY,
    incoming.id,
  ]);
  const merged = mergeMealPlanProjection(current, incoming);
  const detailKey = [MEAL_PLANS_QUERY_KEY, incoming.id];
  if (queryClient.getQueryCache().find({ queryKey: detailKey, exact: true })) {
    queryClient.setQueryData(detailKey, merged);
  }
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const plans = query.state.data;
      if (!Array.isArray(plans)) return;
      queryClient.setQueryData(
        query.queryKey,
        plans.map((plan) =>
          plan.id === incoming.id
            ? mergeMealPlanProjection(plan, incoming)
            : plan,
        ),
      );
    });
  reconcileExistingGroceryProjectionsForPlan(queryClient, incoming);
  return merged;
}

/**
 * A direct intent was causally retired before it can safely affect the
 * client. This is a definitive cancellation, not a retryable transport
 * failure; its HTTP request may already have reached the server.
 */
export class RetiredDirectCheckoffError extends Error {
  readonly retired = true;

  constructor() {
    super("Direct grocery checkoff was causally retired");
    this.name = "RetiredDirectCheckoffError";
  }
}

export function isRetiredDirectCheckoffError(
  error: unknown,
): error is RetiredDirectCheckoffError {
  return error instanceof RetiredDirectCheckoffError;
}

function safeCheckoffStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

type DirectToggleResult =
  GroceryItemToggleResult | GroceryItemCausalMutationResult;

interface DirectTogglePayload {
  result: GroceryItemToggleResult | undefined;
  role: CoordinatedCheckoff["role"];
  superseded: boolean;
  sharedCausalBoundary: string | undefined;
  operation: CoordinatedCheckoff;
  operationId: string;
  checkoffEpoch: string;
}

export class TerminalDirectCheckoffError extends Error {
  readonly status: number | undefined;
  readonly kind: "policy" | "integrity";
  readonly restoreSnapshot: boolean;

  constructor(
    message: string,
    kind: "policy" | "integrity",
    status?: number,
    restoreSnapshot = false,
  ) {
    super(message);
    this.name = "TerminalDirectCheckoffError";
    this.kind = kind;
    this.status = status;
    this.restoreSnapshot = restoreSnapshot;
  }
}

export function isTerminalDirectCheckoffError(
  error: unknown,
): error is TerminalDirectCheckoffError {
  return error instanceof TerminalDirectCheckoffError;
}

function errorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

function isCausalMutationResult(
  value: DirectToggleResult | undefined,
): value is GroceryItemCausalMutationResult {
  return !!value && typeof value === "object" && "causal" in value;
}

export function isAuthoritativeCausalToggleResult(
  value: unknown,
  request: {
    groceryListId: string;
    itemId: string;
    checked: boolean;
    checkoffEpoch: string;
    operationId: string;
  },
): value is GroceryItemCausalMutationResult {
  if (!value || typeof value !== "object") return false;
  const result = value as Partial<GroceryItemCausalMutationResult>;
  if (
    result.id !== request.itemId ||
    result.groceryListId !== request.groceryListId ||
    typeof result.checked !== "boolean" ||
    !isCanonicalCheckoffEpoch(result.checkoffEpoch) ||
    (result.checkoffOperationId !== null &&
      !parseCheckoffOperationId(result.checkoffOperationId)) ||
    !result.causal ||
    typeof result.causal !== "object"
  ) {
    return false;
  }
  const causal = result.causal;
  if (
    !isCanonicalCheckoffEpoch(causal.acceptedEpoch) ||
    (causal.acceptedOperationId !== null &&
      !parseCheckoffOperationId(causal.acceptedOperationId)) ||
    !isValidServerTimeMs(causal.serverTimeMs) ||
    result.checkoffEpoch !== causal.acceptedEpoch ||
    result.checkoffOperationId !== causal.acceptedOperationId
  ) {
    return false;
  }
  if (causal.outcome === "applied" || causal.outcome === "idempotent") {
    return (
      result.checked === request.checked &&
      causal.acceptedEpoch === request.checkoffEpoch &&
      causal.acceptedOperationId === request.operationId
    );
  }
  if (causal.outcome !== "stale") return false;
  const epochOrder = compareCheckoffEpochs(
    causal.acceptedEpoch,
    request.checkoffEpoch,
  );
  if (epochOrder > 0) return true;
  if (epochOrder < 0 || causal.acceptedOperationId === null) return false;
  const accepted = parseCheckoffOperationId(causal.acceptedOperationId);
  const requested = parseCheckoffOperationId(request.operationId);
  return (
    !!accepted &&
    !!requested &&
    compareCheckoffOperationFreshness(accepted, requested) > 0
  );
}

function isAuthoritativeCausalQuantityResult(
  value: unknown,
  request: {
    groceryListId: string;
    itemId: string;
    quantity: number;
    checkoffEpoch: string;
    checkoffOperationId: string;
  },
): value is GroceryItemCausalMutationResult {
  if (!value || typeof value !== "object") return false;
  const result = value as Partial<GroceryItemCausalMutationResult>;
  if (
    result.id !== request.itemId ||
    result.groceryListId !== request.groceryListId ||
    typeof result.checked !== "boolean" ||
    (result.hasUnquantifiedDemand !== undefined &&
      typeof result.hasUnquantifiedDemand !== "boolean") ||
    typeof result.quantity !== "number" ||
    !Number.isFinite(result.quantity) ||
    (result.hasUnquantifiedDemand === true
      ? result.quantity < 0
      : result.quantity <= 0) ||
    (result.quantityOverride !== null &&
      (typeof result.quantityOverride !== "number" ||
        !Number.isFinite(result.quantityOverride) ||
        result.quantityOverride <= 0)) ||
    !isCanonicalCheckoffEpoch(result.checkoffEpoch) ||
    (result.checkoffOperationId !== null &&
      !parseCheckoffOperationId(result.checkoffOperationId)) ||
    !result.causal ||
    typeof result.causal !== "object"
  ) {
    return false;
  }
  const causal = result.causal;
  if (
    !isCanonicalCheckoffEpoch(causal.acceptedEpoch) ||
    (causal.acceptedOperationId !== null &&
      !parseCheckoffOperationId(causal.acceptedOperationId)) ||
    !isValidServerTimeMs(causal.serverTimeMs) ||
    result.checkoffEpoch !== causal.acceptedEpoch ||
    result.checkoffOperationId !== causal.acceptedOperationId
  ) {
    return false;
  }
  if (causal.outcome === "applied") {
    return (
      result.quantityOverride === request.quantity &&
      causal.acceptedEpoch === incrementCanonicalDecimal(request.checkoffEpoch) &&
      causal.acceptedOperationId === request.checkoffOperationId
    );
  }
  if (causal.outcome === "idempotent") {
    return (
      result.quantityOverride === request.quantity &&
      causal.acceptedEpoch === request.checkoffEpoch &&
      causal.acceptedOperationId === request.checkoffOperationId
    );
  }
  if (causal.outcome !== "stale") return false;
  const epochOrder = compareCheckoffEpochs(
    causal.acceptedEpoch,
    request.checkoffEpoch,
  );
  if (epochOrder > 0) return true;
  if (epochOrder === 0) return true;
  return false;
}

function hasConvergedCausalQuantity(
  result: GroceryItemCausalMutationResult,
  requestedQuantity: number,
): boolean {
  if (result.quantityOverride === requestedQuantity) return true;
  return (
    result.quantityOverride === null &&
    result.hasUnquantifiedDemand !== true &&
    result.quantity === requestedQuantity
  );
}

class ReconciledQuantityMutationError extends TerminalDirectCheckoffError {
  readonly authoritativeItem: CausalItem;
  readonly serverTimeMs: number | undefined;
  readonly quantityOverride: number | null | undefined;

  constructor(
    message: string,
    kind: "policy" | "integrity",
    status: number | undefined,
    authoritativeItem: CausalItem,
    serverTimeMs?: number,
    quantityOverride?: number | null,
  ) {
    super(message, kind, status);
    this.authoritativeItem = authoritativeItem;
    this.serverTimeMs = serverTimeMs;
    this.quantityOverride = quantityOverride;
  }
}

class DuplicateQuantityMutationError extends TerminalDirectCheckoffError {
  readonly duplicate = true;

  constructor() {
    super(
      "That quantity save is already in progress. Wait for it to finish.",
      "integrity",
    );
  }
}

function isDuplicateQuantityMutationError(
  error: unknown,
): error is DuplicateQuantityMutationError {
  return error instanceof DuplicateQuantityMutationError;
}

function isReconciledQuantityMutationError(
  error: unknown,
): error is ReconciledQuantityMutationError {
  return error instanceof ReconciledQuantityMutationError;
}

function mergeAuthoritativeMutationFields<T extends CausalItem>(
  groceryListId: string,
  current: T,
  incoming: CausalItem,
): T {
  return mergeItemAtConfirmedBaseline(groceryListId, current, {
    ...current,
    checked: incoming.checked,
    checkoffEpoch: incoming.checkoffEpoch,
    checkoffOperationId: incoming.checkoffOperationId,
  });
}

/** Reconcile one authoritative mutation row without treating it as a full-list GET. */
export function reconcileAuthoritativeMutationItem(
  queryClient: QueryClient,
  groceryListId: string,
  item: CausalItem,
  serverTimeMs?: number,
): void {
  prepareAndPromoteAuthoritativeItem(
    queryClient,
    groceryListId,
    item,
    serverTimeMs,
  );

  queryClient
    .getQueryCache()
    .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (
        !isGroceryList(current) ||
        current.id !== groceryListId ||
        !current.items.some((candidate) => candidate.id === item.id)
      ) {
        return;
      }
      queryClient.setQueryData(query.queryKey, {
        ...current,
        items: current.items.map((candidate) =>
          candidate.id === item.id
            ? mergeAuthoritativeMutationFields(groceryListId, candidate, item)
            : candidate,
        ),
      });
    });

  const patchPlan = (plan: MealPlan): MealPlan => {
    const list = plan.groceryList;
    if (
      !list ||
      list.id !== groceryListId ||
      !list.items.some((candidate) => candidate.id === item.id)
    ) {
      return plan;
    }
    return {
      ...plan,
      groceryList: {
        ...list,
        items: list.items.map((candidate) =>
          candidate.id === item.id
            ? mergeAuthoritativeMutationFields(groceryListId, candidate, item)
            : candidate,
        ),
      },
    };
  };
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (Array.isArray(current)) {
        queryClient.setQueryData(query.queryKey, current.map(patchPlan));
      } else if (current && typeof current === "object") {
        queryClient.setQueryData(
          query.queryKey,
          patchPlan(current as MealPlan),
        );
      }
    });
}

export function reconcileAuthoritativeCausalState(
  queryClient: QueryClient,
  groceryListId: string,
  itemId: string,
  checked: boolean,
  checkoffEpoch: string,
  checkoffOperationId: string | null,
  serverTimeMs?: number,
): void {
  reconcileAuthoritativeMutationItem(queryClient, groceryListId, {
    id: itemId,
    checked,
    checkoffEpoch,
    checkoffOperationId,
  }, serverTimeMs);
}

export function invalidateCheckoffQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
}

function isCurrentDirectRetry(
  checkoffEpoch: string,
  operationId: string,
  groceryListId: string,
  itemId: string,
): boolean {
  const baseline = getConfirmedServerCheckoff(groceryListId, itemId);
  // A direct mutation normally starts after an authoritative GET. If no
  // baseline exists (for example, an offline-first screen mounted before its
  // first fetch), there is no newer server fact that can retire this exact
  // operation locally; preserve the transport error for the normal rollback.
  if (!baseline) return true;
  if (baseline.checkoffEpoch !== checkoffEpoch) return false;
  if (!baseline.checkoffOperationId) return true;
  const operation = parseCheckoffOperationId(operationId);
  const accepted = parseCheckoffOperationId(baseline.checkoffOperationId);
  return (
    !!operation &&
    !!accepted &&
    compareCheckoffOperationFreshness(accepted, operation) <= 0
  );
}

function isDirectCheckoffOperationRetired(
  groceryListId: string,
  itemId: string,
  checkoffEpoch: string,
  operationId: string | undefined,
): boolean {
  if (!operationId || typeof window === "undefined") {
    return false;
  }
  try {
    return isCheckoffOperationRetiredAtBaseline(
      safeCheckoffStorage(),
      { groceryListId, itemId, checkoffEpoch, operationId },
      getConfirmedServerCheckoff(groceryListId, itemId),
    );
  } catch {
    // Blocked storage is intentionally fail-open: without an observable
    // retirement boundary, preserve ordinary request and rollback behavior.
    return false;
  }
}

function publishSuccessfulDirectCheckoffRetirement(
  groceryListId: string,
  itemId: string,
  supersededThroughOperationId: string | undefined,
  supersededThroughEpoch: string | undefined,
): void {
  if (!supersededThroughOperationId || !supersededThroughEpoch) return;
  try {
    // The retirement module merges and notifies synchronously even if its
    // durable write fails. Passing null after a blocked localStorage getter
    // deliberately preserves that same-realm causal knowledge.
    retireCheckoffOperationsThrough(
      safeCheckoffStorage(),
      groceryListId,
      itemId,
      supersededThroughOperationId,
      supersededThroughEpoch,
    );
  } catch {
    // A storage accessor or subscriber must not turn an accepted server
    // success into a rejected client mutation. The retirement merge happens
    // before subscriber notification, so valid same-realm knowledge remains.
  }
}

function hasStrictlyNewerDirectCheckoffRetirement(
  groceryListId: string,
  itemId: string,
  checkoffEpoch: string | undefined,
  supersededThroughOperationId: string | undefined,
): boolean {
  const successfulBoundary = parseCheckoffOperationId(
    supersededThroughOperationId,
  );
  if (!checkoffEpoch || !successfulBoundary) return false;
  try {
    const maximum = loadAdmittedCheckoffRetirementV3Maxima(
      safeCheckoffStorage(),
      (listId, targetItemId) =>
        getConfirmedServerCheckoff(listId, targetItemId),
    ).find(
      (record) =>
        record.groceryListId === groceryListId && record.itemId === itemId,
    );
    if (!maximum) return false;
    const epochOrder = compareCheckoffEpochs(
      maximum.supersededThroughEpoch,
      checkoffEpoch,
    );
    if (epochOrder !== 0) return epochOrder > 0;
    return (
      compareCheckoffOperationFreshness(
        parseCheckoffOperationId(maximum.supersededThroughOperationId)!,
        successfulBoundary,
      ) > 0
    );
  } catch {
    return false;
  }
}

export interface UseGroceryListByMealPlanOptions {
  /**
   * Overrides the default 5s poll. Pass `false` to disable polling
   * entirely. Same query key regardless of this option, so a Home-page
   * call and a Groceries-page call for the same mealPlanId share one
   * cache entry -- TanStack Query just applies whichever interval the
   * currently-mounted observer(s) request.
   */
  refetchInterval?: number | false;
  /**
   * By-aisle (default, omitted) or by-recipe (Track D, D2, issue #383)
   * server-side grouping. Included in the query key so the two groupings
   * cache separately -- switching the seg toggle refetches rather than
   * showing stale grouping from the other mode.
   */
  groupBy?: GroceryGroupBy;
  /**
   * Opt-in keepPreviousData across a query-key change (D2 re-review
   * P3+N1). ONLY the Groceries view should pass this (it keeps the
   * By aisle/By recipe toggle mounted during the first flip) -- consumers
   * whose key changes because the PLAN changed (Dashboard/MealPlanner
   * chips after generating a new week) must not, or they'd briefly show
   * the previous plan's numbers as if they were current.
   */
  keepPreviousOnKeyChange?: boolean;
}

/**
 * Fetch grocery list by meal plan ID.
 *
 * Defaults to the Groceries page's real-time shopping behavior (household
 * members shop simultaneously -- poll so the other phone's checkoffs land
 * within ~5s; spec: no websockets in v1). Callers that only need the list
 * for a lightweight summary (e.g. Home's aisle chips) should pass a longer
 * `refetchInterval` or `false` -- Home doesn't need 5s freshness and
 * shouldn't multiply the polling load of every open tab.
 */
export function useGroceryListByMealPlan(
  mealPlanId: string,
  options: UseGroceryListByMealPlanOptions = {},
) {
  const queryClient = useQueryClient();
  // Household members shop simultaneously -- poll by default so the other
  // phone's checkoffs land within ~5s (spec: no websockets in v1). Callers
  // that only want a one-shot read (e.g. Home's aisle chips or the Planner's
  // stat chips, which have no shopping-together use case) pass
  // `refetchInterval: false`.
  const {
    refetchInterval = 5_000,
    groupBy,
    keepPreviousOnKeyChange = false,
  } = options;

  return useQuery<GroceryList>({
    queryKey: [
      GROCERY_LISTS_QUERY_KEY,
      "meal-plan",
      mealPlanId,
      groupBy ?? "aisle",
    ],
    queryFn: async ({ signal }) => {
      const incoming = await groceryListApi.getGroceryListByMealPlan(
        mealPlanId,
        groupBy,
      );
      return signal.aborted
        ? incoming
        : reconcileAuthoritativeGroceryList(queryClient, incoming, [
            GROCERY_LISTS_QUERY_KEY,
            "meal-plan",
            mealPlanId,
            groupBy ?? "aisle",
          ]);
    },
    enabled: !!mealPlanId,
    refetchInterval,
    refetchIntervalInBackground: false,
    // D2 re-review P3 + N1: the groupBy-suffixed key has no cached data
    // the first time the user flips the By aisle/By recipe toggle --
    // without a placeholder, the whole list (toggle included) is replaced
    // by the loading spinner until the fetch lands. OPT-IN (GroceryList
    // passes it) rather than global: Dashboard/MealPlanner's chips also
    // use this hook, and a global keepPreviousData would briefly show the
    // PREVIOUS plan's numbers after generating a new plan -- a stale-data
    // honesty problem worse than their brief loading state.
    ...(keepPreviousOnKeyChange && {
      placeholderData: (
        previousData: GroceryList | undefined,
        previousQuery: { queryKey: readonly unknown[] } | undefined,
      ) => {
        const previousPlanId = previousQuery?.queryKey[2];

        // Keeping a list while only its grouping changes prevents a loading
        // flash. Crossing plan identities (including plan -> no plan) would
        // mislabel the old plan's ingredients as current, so expose no data.
        return mealPlanId && previousPlanId === mealPlanId
          ? previousData
          : undefined;
      },
    }),
  });
}

/**
 * Fetch grocery list by ID
 */
export function useGroceryList(id: string) {
  const queryClient = useQueryClient();
  return useQuery<GroceryList>({
    queryKey: [GROCERY_LISTS_QUERY_KEY, id],
    queryFn: async ({ signal }) => {
      const incoming = await groceryListApi.getGroceryList(id);
      return signal.aborted
        ? incoming
        : reconcileAuthoritativeGroceryList(queryClient, incoming, [
            GROCERY_LISTS_QUERY_KEY,
            id,
          ]);
    },
    enabled: !!id,
  });
}

/**
 * Generate a grocery list for a meal plan
 */
export function useGenerateGroceryList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mealPlanId: string) =>
      groceryListApi.generateGroceryList(mealPlanId),
    onSuccess: (data, mealPlanId) => {
      // Invalidate grocery lists for this meal plan
      queryClient.invalidateQueries({
        queryKey: [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlanId],
      });
    },
  });
}

/**
 * Manually add an item to a meal plan's grocery list (the pantry "Running
 * low" card's add-to-list action -- issue #374). Invalidates both caches
 * the list can surface through: the full grocery-list query and the
 * meal-plans list's embedded checked-count projection.
 */
type PreparedManualBoundary = {
  listId: string;
  operationId: string;
  baseline?: CausalItem;
};

// Direct evidence reads are not owned by QueryClient's cancellation machinery.
// A later acquisition or confirmed absence must fence their side effects too.
const manualAcquisitions = new WeakMap<QueryClient, Map<string, object>>();
function beginManualAcquisition(
  queryClient: QueryClient,
  mealPlanId: string,
): object {
  let plans = manualAcquisitions.get(queryClient);
  if (!plans) {
    plans = new Map();
    manualAcquisitions.set(queryClient, plans);
  }
  const generation = {};
  plans.set(mealPlanId, generation);
  return generation;
}

function manualConflict(
  message = "The add was not applied. Review the current quantity before adding again.",
): Error {
  return new TerminalDirectCheckoffError(message, "integrity");
}

async function cancelManualQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.cancelQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] }),
    queryClient.cancelQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] }),
  ]);
}

/** Embedded meal-plan rows and optimistic caches are never preparation evidence. */
async function acquireManualList(
  queryClient: QueryClient,
  mealPlanId: string,
  expectedListId?: string,
): Promise<GroceryList> {
  const generation = beginManualAcquisition(queryClient, mealPlanId);
  await cancelManualQueries(queryClient);
  const incoming = await groceryListApi.getGroceryListByMealPlan(
    mealPlanId,
    "aisle",
  );
  if (manualAcquisitions.get(queryClient)?.get(mealPlanId) !== generation) {
    throw manualConflict(
      "The list changed while this action was being prepared. Review the current list before trying again.",
    );
  }
  const snapshot = decodeAuthoritativeGroceryCheckoffSnapshot(incoming);
  if (
    !snapshot ||
    incoming.mealPlanId !== mealPlanId ||
    (expectedListId !== undefined && incoming.id !== expectedListId) ||
    new Set(snapshot.items.map((item) => item.id)).size !==
      snapshot.items.length ||
    incoming.items.some((item) => item.groceryListId !== incoming.id)
  ) {
    throw manualConflict(
      "A fresh grocery list could not be verified. Refresh before trying again.",
    );
  }
  const key = [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlanId, "aisle"];
  const merged = reconcileAuthoritativeGroceryList(queryClient, incoming, key);
  queryClient.setQueryData(key, merged);
  return incoming;
}

function sameManualBoundary(
  left: CausalItem | undefined,
  right: CausalItem,
): boolean {
  return (
    !!left &&
    left.checked === right.checked &&
    left.checkoffEpoch === right.checkoffEpoch &&
    left.checkoffOperationId === right.checkoffOperationId
  );
}

function prepareManualExistingOperation(
  list: GroceryList,
  item: CausalItem,
): PreparedManualBoundary {
  const baseline = getConfirmedServerCheckoff(list.id, item.id);
  if (!sameManualBoundary(baseline && { id: item.id, ...baseline }, item)) {
    throw manualConflict(
      "That item needs a fresh server state. Refresh before trying again.",
    );
  }
  const storage = safeCheckoffStorage();
  const marker = readPolicyRecoveryMarker(storage, item.id);
  const recovering =
    marker.kind === "valid" || !!loadProcessPolicyRecoveryMarker(item.id);
  const outbox = recovering ? loadOutboxSourcesWithHealth(storage) : undefined;
  const retirements = recovering
    ? loadCheckoffRetirementMaximaWithHealth(storage)
    : undefined;
  const v3Retirements = recovering
    ? loadCheckoffRetirementV3MaximaWithHealth(storage)
    : undefined;
  const health = [outbox?.health, retirements?.health, v3Retirements?.health];
  const generated = createPolicySafeCheckoffOperationId({
    storage,
    target: { groceryListId: list.id, itemId: item.id },
    baseline,
    baselineItemId: item.id,
    authoritativeServerTimeMs: list.serverTimeMs,
    observedEntries:
      outbox?.sources
        .flatMap((source) => source.entries)
        .filter(
          (entry) =>
            entry.groceryListId === list.id && entry.itemId === item.id,
        ) ?? [],
    observedRetirementOperationIds: [
      ...(retirements?.maxima ?? []),
      ...(v3Retirements?.maxima ?? []),
    ]
      .filter(
        (entry) => entry.groceryListId === list.id && entry.itemId === item.id,
      )
      .map((entry) => entry.supersededThroughOperationId),
    ordinaryRetirementOperationIds: [],
    recoverySourcesComplete: recovering,
    recoverySourceHealth: {
      complete: health.every((source) => source?.complete !== false),
      malformed: health.some((source) => source?.malformed),
      unreadable: health.some((source) => source?.unreadable),
    },
  });
  if (generated.kind !== "created")
    throw manualConflict(
      "Your device needs a fresh server state before this change can be sent.",
    );
  return {
    listId: list.id,
    operationId: generated.operationId,
    baseline: { ...item },
  };
}

/** Validate the server row and its causal summary before either can be promoted. */
function isManualMutationResult(
  value: unknown,
  prepared: PreparedManualBoundary,
): value is GroceryItemCausalMutationResult {
  if (!value || typeof value !== "object") return false;
  const item = value as GroceryItemCausalMutationResult;
  const causal = item.causal;
  return (
    item.groceryListId === prepared.listId &&
    (!prepared.baseline || item.id === prepared.baseline.id) &&
    typeof item.quantity === "number" &&
    Number.isFinite(item.quantity) &&
    item.quantity >= 0 &&
    (item.manualQuantity === null ||
      (typeof item.manualQuantity === "number" &&
        Number.isFinite(item.manualQuantity) &&
        item.manualQuantity >= 0)) &&
    typeof item.unit === "string" &&
    item.unit.length > 0 &&
    Array.isArray(item.sourceRecipeIds) &&
    !!causal &&
    ["applied", "idempotent", "stale"].includes(causal.outcome) &&
    causal.acceptedEpoch === item.checkoffEpoch &&
    causal.acceptedOperationId === item.checkoffOperationId &&
    groceryListApi.isManualGroceryItemPreparation({
      groceryListId: item.groceryListId,
      serverTimeMs: causal.serverTimeMs,
      target: {
        kind: "existing",
        itemId: item.id,
        checked: item.checked,
        checkoffEpoch: item.checkoffEpoch,
        checkoffOperationId: item.checkoffOperationId,
      },
    })
  );
}

function isOwnManualSuccess(
  item: CausalItem,
  prepared: PreparedManualBoundary,
): boolean {
  return (
    (!prepared.baseline || item.id === prepared.baseline.id) &&
    item.checkoffEpoch ===
      (prepared.baseline
        ? incrementCanonicalDecimal(prepared.baseline.checkoffEpoch!)
        : "0") &&
    item.checkoffOperationId === prepared.operationId
  );
}

function isUncertainManualTransport(error: unknown): boolean {
  const status = errorStatus(error);
  return status === 0 || (status !== undefined && status >= 500);
}

function reconcileManualPolicyRejection(
  queryClient: QueryClient,
  prepared: PreparedManualBoundary,
  error: unknown,
): void {
  const rejection = parseCheckoffCausalTransportError(error);
  if (rejection?.kind !== "policy-rejected" || !prepared.baseline) return;
  const details = rejection.details;
  if (
    details.oldEpoch !== prepared.baseline.checkoffEpoch ||
    details.rejectedOperationId !== prepared.operationId
  )
    return;
  const itemId = prepared.baseline.id;
  const confirmed = getConfirmedServerCheckoff(prepared.listId, itemId);
  resetCheckoffOperationProcessClock();
  if (
    !confirmed ||
    compareCheckoffEpochs(confirmed.checkoffEpoch, details.newEpoch) <= 0
  ) {
    purgeConfirmedCheckoffTargetState(
      safeCheckoffStorage(),
      prepared.listId,
      itemId,
      details.newEpoch,
    );
    savePolicyRecoveryMarker(safeCheckoffStorage(), {
      schemaVersion: 1,
      itemId,
      serverTimeMs: details.serverTimeMs,
      oldEpoch: details.oldEpoch,
      newEpoch: details.newEpoch,
      rejectedOperationId: details.rejectedOperationId,
      policyCeiling: CHECKOFF_POLICY_CEILING,
    });
  }
  reconcileAuthoritativeMutationItem(
    queryClient,
    prepared.listId,
    {
      id: itemId,
      checked: details.checked,
      checkoffEpoch: details.newEpoch,
      checkoffOperationId: null,
    },
    details.serverTimeMs,
  );
}

export function useAddGroceryItem(mealPlanId: string) {
  const queryClient = useQueryClient();
  const pending = useRef(false);
  return useMutation({
    networkMode: "always",
    retry: false,
    mutationFn: async (data: AddGroceryItemRequest) => {
      if (pending.current)
        throw manualConflict("An add is already in progress.");
      pending.current = true;
      // Nested catalogue selection is part of the fingerprint too. Never replay
      // a caller-owned object that may have changed while the request was pending.
      const intent: AddGroceryItemRequest = Object.freeze({
        ...data,
        ...("retailerProduct" in data && data.retailerProduct
          ? { retailerProduct: Object.freeze({ ...data.retailerProduct }) }
          : {}),
      });
      try {
        let prepared: PreparedManualBoundary | undefined;
        let payload: AddGroceryItemRequestWithCausality | undefined;
        for (let attempt = 0; attempt < 2; attempt++) {
          const list = await acquireManualList(queryClient, mealPlanId);
          let preparation;
          try {
            preparation = await groceryListApi.prepareManualGroceryItem(
              mealPlanId,
              intent,
            );
          } catch (error) {
            const code = (error as { data?: { code?: unknown } })?.data?.code;
            if (
              attempt === 0 &&
              errorStatus(error) === 409 &&
              code === "GROCERY_LIST_NOT_READY"
            )
              continue;
            throw error;
          }
          if (!groceryListApi.isManualGroceryItemPreparation(preparation))
            throw manualConflict(
              "The grocery preparation could not be verified.",
            );
          if (preparation.groceryListId !== list.id) continue;
          const target = preparation.target;
          if (target.kind === "existing") {
            const item = list.items.find(
              (candidate) => candidate.id === target.itemId,
            );
            if (!sameManualBoundary(item, { id: target.itemId, ...target }))
              continue;
            prepared = prepareManualExistingOperation(list, item!);
            payload = {
              ...intent,
              causalTarget: {
                kind: "existing",
                itemId: target.itemId,
                checkoffEpoch: target.checkoffEpoch,
                operationId: prepared.operationId,
              },
            };
          } else {
            const generated = createPolicySafeAbsentCheckoffOperationId({
              storage: safeCheckoffStorage(),
              authoritativeServerTimeMs: preparation.serverTimeMs,
            });
            if (generated.kind !== "created")
              throw manualConflict(
                "Your device needs a fresh server state before this add can be sent.",
              );
            prepared = { listId: list.id, operationId: generated.operationId };
            payload = {
              ...intent,
              causalTarget: {
                kind: "create",
                expectedAbsent: true,
                operationId: generated.operationId,
              },
            };
          }
          Object.freeze(payload.causalTarget);
          Object.freeze(payload);
          break;
        }
        if (!prepared || !payload) throw manualConflict();
        for (let send = 0; send < 2; send++) {
          let response;
          try {
            response = await groceryListApi.addGroceryItem(mealPlanId, payload);
          } catch (error) {
            if (!isUncertainManualTransport(error)) {
              reconcileManualPolicyRejection(queryClient, prepared, error);
              await acquireManualList(
                queryClient,
                mealPlanId,
                prepared.listId,
              ).catch(() => undefined);
              throw error;
            }
            const fresh = await acquireManualList(
              queryClient,
              mealPlanId,
              prepared.listId,
            );
            const own = fresh.items.find((item) =>
              isOwnManualSuccess(item, prepared!),
            );
            if (own) return own;
            if (
              prepared.baseline &&
              send === 0 &&
              sameManualBoundary(
                fresh.items.find((item) => item.id === prepared!.baseline!.id),
                prepared.baseline,
              )
            )
              continue;
            throw manualConflict(
              "Could not confirm the add. Review the refreshed list; use Add again only if you still want a new addition.",
            );
          }
          if (!isManualMutationResult(response, prepared)) {
            // A malformed 2xx may still describe a committed write. Recover
            // through GET only; replaying here can duplicate an absent create.
            const fresh = await acquireManualList(
              queryClient,
              mealPlanId,
              prepared.listId,
            ).catch(() => undefined);
            const own = fresh?.items.find((item) =>
              isOwnManualSuccess(item, prepared!),
            );
            if (own) return own;
            throw manualConflict(
              "Could not confirm the add. Review the refreshed list; use Add again only if you still want a new addition.",
            );
          }
          reconcileAuthoritativeMutationItem(
            queryClient,
            prepared.listId,
            response,
            response.causal.serverTimeMs,
          );
          const ownSuccess =
            isOwnManualSuccess(response, prepared) &&
            (prepared.baseline || response.causal.outcome !== "stale");
          // A successful write stays confirmed even if the subsequent display GET
          // fails. Reporting failure here could cause another additive intent.
          await acquireManualList(
            queryClient,
            mealPlanId,
            prepared.listId,
          ).catch(() => undefined);
          if (!ownSuccess) throw manualConflict();
          return response;
        }
        throw manualConflict();
      } finally {
        pending.current = false;
      }
    },
    onSettled: () => invalidateCheckoffQueries(queryClient),
  });
}

async function applyManualAbsence(
  queryClient: QueryClient,
  mealPlanId: string,
  listId: string,
  itemId: string,
): Promise<void> {
  beginManualAcquisition(queryClient, mealPlanId);
  await cancelManualQueries(queryClient);
  purgeDeletedCheckoffTargetState(safeCheckoffStorage(), listId, itemId);
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (isGroceryList(current) && current.id === listId)
        queryClient.setQueryData(query.queryKey, {
          ...current,
          items: current.items.filter((item) => item.id !== itemId),
        });
    });
  const without = (plan: MealPlan): MealPlan =>
    plan.groceryList?.id === listId
      ? {
          ...plan,
          groceryList: {
            ...plan.groceryList,
            items: plan.groceryList.items.filter((item) => item.id !== itemId),
          },
        }
      : plan;
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const current = query.state.data;
      if (Array.isArray(current))
        queryClient.setQueryData(query.queryKey, current.map(without));
      else if (current && typeof current === "object")
        queryClient.setQueryData(query.queryKey, without(current as MealPlan));
    });
}

/** Remove a manual extra without suppressing any plan-derived contribution. */
export function useDeleteManualGroceryItem(mealPlanId: string) {
  const queryClient = useQueryClient();
  const pending = useRef(new Set<string>());
  return useMutation({
    networkMode: "always",
    retry: false,
    mutationFn: async (itemId: string) => {
      if (pending.current.has(itemId))
        throw manualConflict("That removal is already in progress.");
      pending.current.add(itemId);
      try {
        const list = await acquireManualList(queryClient, mealPlanId);
        const item = list.items.find((candidate) => candidate.id === itemId);
        if (!item || item.manualQuantity == null || item.manualQuantity <= 0)
          throw manualConflict(
            "That manual extra is no longer available. Review the refreshed list.",
          );
        const prepared = prepareManualExistingOperation(list, item);
        let response;
        try {
          response = await groceryListApi.deleteManualGroceryItem(
            mealPlanId,
            itemId,
            {
              checkoffEpoch: prepared.baseline!.checkoffEpoch!,
              checkoffOperationId: prepared.operationId,
            },
          );
        } catch (error) {
          if (
            !isUncertainManualTransport(error) &&
            errorStatus(error) !== 404
          ) {
            reconcileManualPolicyRejection(queryClient, prepared, error);
            await acquireManualList(queryClient, mealPlanId, list.id).catch(
              () => undefined,
            );
            throw error;
          }
          const fresh = await acquireManualList(
            queryClient,
            mealPlanId,
            list.id,
          );
          const surviving = fresh.items.find(
            (candidate) => candidate.id === itemId,
          );
          if (!surviving) {
            await applyManualAbsence(queryClient, mealPlanId, list.id, itemId);
            return null;
          }
          if (
            surviving.manualQuantity === null &&
            isOwnManualSuccess(surviving, prepared)
          )
            return surviving;
          throw manualConflict(
            "Could not confirm the removal. Review the refreshed list before trying again.",
          );
        }
        if (response === null && item.sourceRecipeIds.length === 0) {
          await applyManualAbsence(queryClient, mealPlanId, list.id, itemId);
          const fresh = await acquireManualList(
            queryClient,
            mealPlanId,
            list.id,
          );
          if (fresh.items.some((candidate) => candidate.id === itemId))
            throw manualConflict(
              "The removed item could not be reconciled. Refresh the list.",
            );
          return null;
        }
        if (!isManualMutationResult(response, prepared)) {
          const fresh = await acquireManualList(
            queryClient,
            mealPlanId,
            list.id,
          );
          const surviving = fresh.items.find(
            (candidate) => candidate.id === itemId,
          );
          if (!surviving) {
            await applyManualAbsence(queryClient, mealPlanId, list.id, itemId);
            return null;
          }
          if (
            surviving.manualQuantity === null &&
            isOwnManualSuccess(surviving, prepared)
          )
            return surviving;
          throw manualConflict(
            "Could not confirm the removal. Review the refreshed list before trying again.",
          );
        }
        reconcileAuthoritativeMutationItem(
          queryClient,
          list.id,
          response,
          response.causal.serverTimeMs,
        );
        await acquireManualList(queryClient, mealPlanId, list.id);
        if (
          !isOwnManualSuccess(response, prepared) ||
          response.manualQuantity !== null
        )
          throw manualConflict(
            "The removal was not applied. Review the current quantity before trying again.",
          );
        return response;
      } finally {
        pending.current.delete(itemId);
      }
    },
    onSettled: () => invalidateCheckoffQueries(queryClient),
  });
}

/**
 * Returns a plan with one grocery item's checked state flipped, or the same
 * reference when the plan has no grocery list or does not contain the item.
 * Returning the identity keeps React Query from churning unrelated cache
 * entries.
 */
function patchPlanGroceryItem(
  plan: MealPlan,
  itemId: string,
  checked: boolean,
): MealPlan {
  const list = plan.groceryList;
  if (!list?.items?.some((item) => item.id === itemId)) return plan;

  return {
    ...plan,
    groceryList: {
      ...list,
      items: list.items.map((item) =>
        item.id === itemId ? { ...item, checked } : item,
      ),
    },
  };
}

/**
 * Toggle grocery item checked status
 */
export function useToggleGroceryItem(groceryListId: string) {
  const queryClient = useQueryClient();
  type ToggleVariables = {
    itemId: string;
    checked: boolean;
    supersededThroughOperationId?: string;
  };
  const preparedOperations = useRef(
    new WeakMap<
      ToggleVariables,
      {
        operation: CoordinatedCheckoff;
        operationId: string;
        checkoffEpoch: string;
        wireOperationId: string;
        wireCheckoffEpoch: string;
      }
    >(),
  );

  const prepareOperation = (
    variables: ToggleVariables,
    ready?: Promise<void>,
  ): {
    operation: CoordinatedCheckoff;
    operationId: string;
    checkoffEpoch: string;
    wireOperationId: string;
    wireCheckoffEpoch: string;
  } => {
    const baseline = getConfirmedServerCheckoff(
      groceryListId,
      variables.itemId,
    );
    if (!baseline) {
      throw new TerminalDirectCheckoffError(
        "That item needs a fresh server state. Refresh and try again.",
        "integrity",
      );
    }
    const checkoffEpoch = baseline.checkoffEpoch;
    const storage = safeCheckoffStorage();
    const evidence = getConfirmedServerCheckoffEvidence(
      groceryListId,
      variables.itemId,
    );
    const markerRead = readPolicyRecoveryMarker(storage, variables.itemId);
    const recovering =
      markerRead.kind === "valid" ||
      (markerRead.kind === "absent" &&
        !!loadProcessPolicyRecoveryMarker(variables.itemId));
    const outbox = recovering
      ? loadOutboxSourcesWithHealth(storage)
      : { sources: [], health: { complete: true, malformed: false, unreadable: false } };
    const retirements = recovering
      ? loadCheckoffRetirementMaximaWithHealth(storage)
      : { maxima: [], health: { complete: true, malformed: false, unreadable: false } };
    const v3Retirements = recovering
      ? loadCheckoffRetirementV3MaximaWithHealth(storage)
      : { maxima: [], health: { complete: true, malformed: false, unreadable: false } };
    const observedEntries = recovering
      ? outbox.sources
          .flatMap((source) => source.entries)
          .filter(
            (entry) =>
              entry.groceryListId === groceryListId &&
              entry.itemId === variables.itemId,
          )
      : [];
    const observedRetirementOperationIds = recovering
      ? [...retirements.maxima, ...v3Retirements.maxima]
          .filter(
            (record) =>
              record.groceryListId === groceryListId &&
              record.itemId === variables.itemId,
          )
          .map((record) => record.supersededThroughOperationId)
      : [];
    const recoverySourceHealth = {
      complete:
        outbox.health.complete &&
        retirements.health.complete &&
        v3Retirements.health.complete,
      malformed:
        outbox.health.malformed ||
        retirements.health.malformed ||
        v3Retirements.health.malformed,
      unreadable:
        outbox.health.unreadable ||
        retirements.health.unreadable ||
        v3Retirements.health.unreadable,
    };
    const generated = createPolicySafeCheckoffOperationId({
      storage,
      target: { groceryListId, itemId: variables.itemId },
      baseline,
      baselineItemId: variables.itemId,
      authoritativeServerTimeMs: evidence?.serverTimeMs,
      observedEntries,
      observedRetirementOperationIds,
      ordinaryRetirementOperationIds: variables.supersededThroughOperationId
        ? [variables.supersededThroughOperationId]
        : [],
      recoverySourcesComplete: recovering,
      recoverySourceHealth,
    });
    if (generated.kind === "blocked") {
      throw new TerminalDirectCheckoffError(
        "That item needs a fresh server state. Refresh and try again.",
        "integrity",
      );
    }
    const operationId = generated.operationId;
    const operationRef: { current?: CoordinatedCheckoff } = {};
    const operation = coordinateCheckoff(
      groceryListId,
      variables.itemId,
      variables.checked,
      async () => {
        await (ready ?? Promise.resolve());
        if (
          !operationRef.current?.isCurrentIntent() ||
          !isCurrentDirectRetry(
            checkoffEpoch,
            operationId,
            groceryListId,
            variables.itemId,
          ) ||
          isDirectCheckoffOperationRetired(
            groceryListId,
            variables.itemId,
            checkoffEpoch,
            operationId,
          )
        ) {
          throw new RetiredDirectCheckoffError();
        }
        try {
          return await groceryListApi.toggleGroceryItem(
            groceryListId,
            variables.itemId,
            variables.checked,
            { checkoffEpoch, operationId },
          );
        } catch (error) {
          // Construct the retry capability at the one actual wire boundary.
          // Every coordinator participant observes this same rejection object,
          // so joiners cannot substitute an operation they never sent.
          if (isRetryableCheckoffError(error)) {
            throw issueRetryableDirectCheckoffError(
              error,
              {
                groceryListId,
                itemId: variables.itemId,
                checked: variables.checked,
                checkoffEpoch,
                operationId,
              },
            );
          }
          throw error;
        }
      },
      variables.supersededThroughOperationId,
      checkoffEpoch,
      operationId,
    );
    // The async send callback yields before reading this holder, allowing the
    // coordinator to publish the live ownership handle first.
    operationRef.current = operation;
    if (!operation.operationId) {
      throw new TerminalDirectCheckoffError(
        "That item needs a fresh server state. Refresh and try again.",
        "integrity",
      );
    }
    return {
      operation,
      operationId,
      checkoffEpoch,
      wireOperationId: operation.operationId,
      wireCheckoffEpoch: operation.checkoffEpoch ?? checkoffEpoch,
    };
  };

  return useMutation({
    // Offline failures must reach the durable outbox instead of pausing in memory.
    networkMode: "always",
    retry: false,
    mutationFn: async (variables: ToggleVariables) => {
      const { itemId } = variables;
      const prepared =
        preparedOperations.current.get(variables) ??
        prepareOperation(variables);
      const {
        operation,
        operationId,
        checkoffEpoch,
        wireOperationId,
        wireCheckoffEpoch,
      } = prepared;
      preparedOperations.current.delete(variables);
      if (operation.role === "stale") {
        return {
          result: undefined,
          role: operation.role,
          superseded: true,
          sharedCausalBoundary: operation.sharedCausalBoundary(),
          operation,
          operationId,
          checkoffEpoch,
        } satisfies DirectTogglePayload;
      }
      let result: GroceryItemToggleResult | undefined;
      try {
        result = await operation.promise;
        if (
          !isAuthoritativeCausalToggleResult(result, {
            groceryListId,
            itemId,
            checked: variables.checked,
            checkoffEpoch: wireCheckoffEpoch,
            operationId: wireOperationId,
          })
        ) {
          throw new TerminalDirectCheckoffError(
            "The server returned an invalid check-off result. Refresh and try again.",
            "integrity",
            undefined,
            true,
          );
        }
      } catch (error) {
        const retryFailure = isRetryableDirectCheckoffError(error)
          ? error
          : undefined;
        const failedEpoch = retryFailure?.checkoffEpoch ?? checkoffEpoch;
        const failedOperationId = retryFailure?.operationId ?? operationId;
        // An already-on-wire request cannot be recalled, but a newer direct
        // success owns client-visible state once its retirement is observed.
        // Translate both definite and retryable transport failures so the
        // component neither toasts nor queues stale work.
        if (
          isDirectCheckoffOperationRetired(
            groceryListId,
            itemId,
            failedEpoch,
            failedOperationId,
          )
        ) {
          throw new RetiredDirectCheckoffError();
        }
        const causalError = parseCheckoffCausalTransportError(error);
        const status = errorStatus(error);
        if (status === 404) {
          purgeDeletedCheckoffTargetState(
            safeCheckoffStorage(),
            groceryListId,
            itemId,
          );
          invalidateCheckoffQueries(queryClient);
          throw new TerminalDirectCheckoffError(
            "That grocery item is no longer available.",
            "integrity",
            status,
          );
        } else if (causalError?.kind === "policy-rejected") {
          const details = causalError.details;
          if (
            details.oldEpoch !== checkoffEpoch ||
            details.rejectedOperationId !== operationId
          ) {
            throw new TerminalDirectCheckoffError(
              "That item changed on another device. Refresh and try again.",
              "integrity",
              status,
              true,
            );
          }
          purgeConfirmedCheckoffTargetState(
            safeCheckoffStorage(),
            groceryListId,
            itemId,
            details.newEpoch,
          );
          confirmServerCheckoff(groceryListId, itemId, {
            checked: details.checked,
            checkoffEpoch: details.newEpoch,
            checkoffOperationId: null,
          }, details.serverTimeMs);
          const marker: CheckoffPolicyRecoveryMarkerV1 = {
            schemaVersion: 1,
            itemId,
            serverTimeMs: details.serverTimeMs,
            oldEpoch: details.oldEpoch,
            newEpoch: details.newEpoch,
            rejectedOperationId: details.rejectedOperationId,
            policyCeiling: CHECKOFF_POLICY_CEILING,
          };
          // The rejected ID may have poisoned the realm's process maximum.
          // Drop it before a later recovery attempt can compare against it;
          // the marker/fallback remains the sole recovery admission gate.
          resetCheckoffOperationProcessClock();
          savePolicyRecoveryMarker(safeCheckoffStorage(), marker);
          reconcileAuthoritativeCausalState(
            queryClient,
            groceryListId,
            itemId,
            details.checked,
            details.newEpoch,
            null,
          );
          invalidateCheckoffQueries(queryClient);
          throw new TerminalDirectCheckoffError(
            "Your device clock is out of sync. Check it and try again.",
            "policy",
            status,
          );
        } else if (causalError?.kind === "required") {
          const details = causalError.details;
          if (details.acceptedEpoch) {
            reconcileAuthoritativeCausalState(
              queryClient,
              groceryListId,
              itemId,
              details.checked,
              details.acceptedEpoch,
              details.acceptedOperationId,
            );
          }
          invalidateCheckoffQueries(queryClient);
          throw new TerminalDirectCheckoffError(
            "That item needs a fresh server state. Refresh and try again.",
            "integrity",
            status,
          );
        } else if (status === 409 || status === 428) {
          throw new TerminalDirectCheckoffError(
            "That item changed on another device. Refresh and try again.",
            "integrity",
            status,
            true,
          );
        }

        if (
          retryFailure &&
          (!operation.isCurrentIntent() ||
            !isCurrentDirectRetry(
              retryFailure.checkoffEpoch,
              retryFailure.operationId,
              groceryListId,
              itemId,
            ))
        ) {
          throw new RetiredDirectCheckoffError();
        }
        if (retryFailure) throw retryFailure;
        throw error;
      }
      return {
        result,
        role: operation.role,
        superseded: operation.isSuperseded(),
        sharedCausalBoundary: operation.sharedCausalBoundary(),
        operation,
        operationId,
        checkoffEpoch,
      } satisfies DirectTogglePayload;
    },
    onMutate: async (variables: ToggleVariables) => {
      const { itemId, checked } = variables;
      let releaseRequest!: () => void;
      let rejectRequest!: (reason: unknown) => void;
      const ready = new Promise<void>((resolve, reject) => {
        releaseRequest = resolve;
        rejectRequest = reject;
      });
      const prepared = prepareOperation(variables, ready);
      const { operation, operationId } = prepared;
      preparedOperations.current.set(variables, prepared);

      const localRevision =
        operation.role === "owner"
          ? registerLocalRevision(groceryListId, itemId, checked, operationId)
          : undefined;

      if (operation.role === "joined") {
        // The owner already applied this exact optimistic target. A joined
        // caller must not take a second snapshot that could later restore the
        // owner's optimistic value instead of the shared baseline.
        releaseRequest();
        return { operation, localRevision };
      }

      try {
        // Cancel outgoing queries to avoid overwriting optimistic updates.
        // The lane is reserved before this await so a later opposite tap can
        // supersede this intent even while cache cancellation is pending.
        await Promise.all([
          queryClient.cancelQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] }),
          queryClient.cancelQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] }),
        ]);

        // Snapshot previous values for rollback
        const previousGroceryLists = queryClient.getQueriesData({
          queryKey: [GROCERY_LISTS_QUERY_KEY],
        });
        const previousMealPlans = queryClient.getQueriesData({
          queryKey: [MEAL_PLANS_QUERY_KEY],
        });

        // Optimistically update all grocery list queries
        queryClient.setQueriesData<GroceryList>(
          { queryKey: [GROCERY_LISTS_QUERY_KEY] },
          (old) => {
            if (!old || !old.items) return old;

            return {
              ...old,
              items: old.items.map((item: GroceryItem) =>
                item.id === itemId ? { ...item, checked } : item,
              ),
            };
          },
        );

        // The Dashboard reads grocery counts from the meal-plans cache, not
        // the grocery-lists cache. Patch it in the same pass so Home updates
        // immediately -- invalidating instead would refetch every meal plan on
        // every single checkbox tap.
        queryClient.setQueriesData<MealPlan | MealPlan[]>(
          { queryKey: [MEAL_PLANS_QUERY_KEY] },
          (old) => {
            if (!old) return old;
            return Array.isArray(old)
              ? old.map((plan) => patchPlanGroceryItem(plan, itemId, checked))
              : patchPlanGroceryItem(old, itemId, checked);
          },
        );

        releaseRequest();
        return {
          operation,
          operationId,
          localRevision,
          previousGroceryLists,
          previousMealPlans,
        };
      } catch (error) {
        preparedOperations.current.delete(variables);
        if (localRevision) {
          clearLocalRevisionIfCurrent(
            groceryListId,
            itemId,
            localRevision.revision,
          );
        }
        rejectRequest(error);
        throw error;
      }
    },
    onError: (error, variables, context) => {
      if (isRetiredDirectCheckoffError(error)) {
        // The request was retired either immediately before dispatch or while
        // already on-wire. Never restore its stale snapshot over the newer
        // authoritative success that published the retirement.
        if (
          context?.operation.role === "owner" &&
          context.operation.isCurrentIntent()
        ) {
          if (context.localRevision) {
            clearLocalRevisionIfCurrent(
              groceryListId,
              variables.itemId,
              context.localRevision.revision,
            );
          }
        }
        invalidateCheckoffQueries(queryClient);
        return;
      }

      if (isTerminalDirectCheckoffError(error)) {
        if (context?.localRevision) {
          clearLocalRevisionIfCurrent(
            groceryListId,
            variables.itemId,
            context.localRevision.revision,
          );
        }
        if (
          error.restoreSnapshot &&
          context?.operation.role === "owner" &&
          context.operation.isCurrentIntent()
        ) {
          context.previousGroceryLists?.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          });
          context.previousMealPlans?.forEach(([queryKey, data]) => {
            queryClient.setQueryData(queryKey, data);
          });
        }
        // Recognized causal terminal responses are reconciled before mutation
        // settlement. Unknown/current-owner terminals restore their own
        // snapshots before invalidation because they carry no authority.
        invalidateCheckoffQueries(queryClient);
        return;
      }

      // Only the owner of the latest intent may restore its baseline. Joined
      // callbacks share that owner's request, and superseded owners have a
      // newer optimistic target that must remain visible.
      if (
        context?.operation.role !== "owner" ||
        !context.operation.isCurrentIntent()
      ) {
        return;
      }

      // A newer direct success in another tab can retire this request after
      // it was dispatched but before it rejects. Consult the shared maximum
      // captured for this request through durable retirement state before
      // restoring either cache snapshot. The newer success is already
      // authoritative, so invalidate both projections and leave the current
      // optimistic state in place until the refetch reconciles it.
      const retiredDirectIntent = isDirectCheckoffOperationRetired(
        groceryListId,
        variables.itemId,
        context?.operation.checkoffEpoch ?? "",
        context?.operationId,
      );

      if (retiredDirectIntent) {
        if (context.localRevision) {
          clearLocalRevisionIfCurrent(
            groceryListId,
            variables.itemId,
            context.localRevision.revision,
          );
        }
        queryClient.invalidateQueries({
          queryKey: [GROCERY_LISTS_QUERY_KEY],
        });
        queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
        return;
      }

      if (context.localRevision) {
        clearLocalRevisionIfCurrent(
          groceryListId,
          variables.itemId,
          context.localRevision.revision,
        );
      }

      context?.previousGroceryLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      context?.previousMealPlans?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (payload, variables, context) => {
      if (!payload.result || !isCausalMutationResult(payload.result)) return;
      publishSuccessfulDirectCheckoffRetirement(
        groceryListId,
        variables.itemId,
        payload.sharedCausalBoundary,
        payload.result.checkoffEpoch,
      );
      const retiredDirectIntent = hasStrictlyNewerDirectCheckoffRetirement(
        groceryListId,
        variables.itemId,
        payload.result.checkoffEpoch,
        payload.sharedCausalBoundary,
      );
      if (
        retiredDirectIntent &&
        context?.operation.role === "owner" &&
        context.operation.isCurrentIntent()
      ) {
        if (context.localRevision) {
          clearLocalRevisionIfCurrent(
            groceryListId,
            variables.itemId,
            context.localRevision.revision,
          );
        }
        // A newer direct success has already committed the opposite target.
        // Invalidation alone preserves this mutation's optimistic value until
        // an asynchronous refetch, so restore the owner snapshot before
        // mutateAsync can settle. The role/current checks preserve a later
        // in-tab intent instead of overwriting it with A's baseline.
        context.previousGroceryLists?.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousMealPlans?.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (!retiredDirectIntent && !payload.superseded) {
        reconcileAuthoritativeMutationItem(
          queryClient,
          groceryListId,
          payload.result,
          payload.result.causal.serverTimeMs,
        );
      }
      if (
        context?.localRevision &&
        context.operation.role === "owner" &&
        context.operation.isCurrentIntent()
      ) {
        clearLocalRevisionIfCurrent(
          groceryListId,
          variables.itemId,
          context.localRevision.revision,
        );
      }
      if (
        payload.role === "owner" &&
        payload.operation.isCurrentIntent() &&
        !payload.superseded &&
        !retiredDirectIntent &&
        payload.result.causal.outcome === "applied"
      ) {
        track("grocery_item_toggled", { purchased: payload.result.checked });

        const previousList = context?.previousGroceryLists
          ?.map(([, value]) => value)
          .find(
            (value): value is GroceryList =>
              isGroceryList(value) && value.id === groceryListId,
          );
        const completion = completionAfterConfirmedCheckoff(
          previousList,
          variables.itemId,
          payload.result.checked,
        );
        if (completion) {
          track("grocery_list_completed", {
            itemCount: completion.itemCount,
            mealPlanId: completion.mealPlanId,
          });
        }
      }

      // Invalidate all grocery list queries to ensure UI stays in sync
      queryClient.invalidateQueries({
        queryKey: [GROCERY_LISTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
  });
}

/**
 * Pin (or un-pin) a grocery item -- the "Already in your pantry" card's
 * "Add back to the list" action (Track D, D2, issue #383). Invalidates
 * every grocery-list query (both groupings share the underlying items, so
 * both need to refetch) the same way useAddGroceryItem does.
 */
export function usePinGroceryItem(groceryListId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, pinned }: { itemId: string; pinned: boolean }) =>
      groceryListApi.pinGroceryItem(groceryListId, itemId, pinned),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
  });
}

/**
 * Applies one field patch to a single item across every cached grocery
 * list, leaving cache entries that are not grocery-list-shaped (the
 * `savings-suggestions` query lives under the same key prefix) untouched.
 * Returns the identity when the item is not in that list, so React Query
 * does not churn unrelated entries.
 */
function patchCachedGroceryItem(
  queryClient: ReturnType<typeof useQueryClient>,
  itemId: string,
  patch: Partial<GroceryItem>,
): void {
  queryClient.setQueriesData<GroceryList>(
    { queryKey: [GROCERY_LISTS_QUERY_KEY] },
    (old) => {
      if (!old || !old.items) return old;
      if (!old.items.some((item) => item.id === itemId)) return old;

      return {
        ...old,
        items: old.items.map((item: GroceryItem) =>
          item.id === itemId ? { ...item, ...patch } : item,
        ),
      };
    },
  );
}

/**
 * Shared optimistic scaffolding for the two line-edit mutations. Cancels
 * in-flight grocery-list queries (the Groceries page polls every 5s -- an
 * unguarded refetch would land on top of the optimistic patch), snapshots
 * for rollback, applies the patch, and hands back the snapshot.
 */
async function beginOptimisticItemPatch(
  queryClient: ReturnType<typeof useQueryClient>,
  itemId: string,
  patch: Partial<GroceryItem>,
) {
  await queryClient.cancelQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
  const previousGroceryLists = queryClient.getQueriesData({
    queryKey: [GROCERY_LISTS_QUERY_KEY],
  });
  patchCachedGroceryItem(queryClient, itemId, patch);
  return { previousGroceryLists };
}

function rollbackGroceryLists(
  queryClient: ReturnType<typeof useQueryClient>,
  context:
    { previousGroceryLists: Array<[readonly unknown[], unknown]> } | undefined,
): void {
  context?.previousGroceryLists?.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
}

function isAtCausalBoundary(
  item: Pick<GroceryItem, "checkoffEpoch" | "checkoffOperationId">,
  checkoffEpoch: string,
  checkoffOperationId: string | null,
): boolean {
  return (
    item.checkoffEpoch === checkoffEpoch &&
    (item.checkoffOperationId ?? null) === checkoffOperationId
  );
}

function retainsConfirmedCausalItem(
  groceryListId: string,
  item: CausalItem,
): boolean {
  const confirmed = getConfirmedServerCheckoff(groceryListId, item.id);
  return !!confirmed && isAtCausalBoundary(
    confirmed,
    item.checkoffEpoch ?? "",
    item.checkoffOperationId ?? null,
  );
}

/**
 * Restore only these optimistic fields when a newer authoritative row has
 * not won. `unitOverride` rides alongside `quantityOverride` here for the
 * same reason it rides alongside it in the request (#1023): the two are
 * written and rolled back as one pair, never independently.
 */
function rollbackQuantityOverrideAtPreparedBoundary(
  queryClient: ReturnType<typeof useQueryClient>,
  groceryListId: string,
  itemId: string,
  prepared: {
    checkoffEpoch: string;
    baselineCheckoffOperationId: string | null;
  },
  context:
    | { previousGroceryLists: Array<[readonly unknown[], unknown]> }
    | undefined,
): void {
  context?.previousGroceryLists.forEach(([queryKey, previous]) => {
    const current = queryClient.getQueryData(queryKey);
    if (!isGroceryList(previous) || !isGroceryList(current)) return;
    if (previous.id !== groceryListId || current.id !== groceryListId) return;
    const previousItem = previous.items.find((item) => item.id === itemId);
    const currentItem = current.items.find((item) => item.id === itemId);
    if (!previousItem || !currentItem) return;
    const currentHasCausalTuple = currentItem.checkoffEpoch !== undefined;
    const stillPreparedBoundary = currentHasCausalTuple
      ? isAtCausalBoundary(
          currentItem,
          prepared.checkoffEpoch,
          prepared.baselineCheckoffOperationId,
        )
      : retainsConfirmedCausalItem(groceryListId, {
          id: itemId,
          checked: currentItem.checked,
          checkoffEpoch: prepared.checkoffEpoch,
          checkoffOperationId: prepared.baselineCheckoffOperationId,
        });
    if (!stillPreparedBoundary) return;
    queryClient.setQueryData(queryKey, {
      ...current,
      items: current.items.map((item) =>
        item.id === itemId
          ? {
              ...item,
              quantityOverride: previousItem.quantityOverride,
              unitOverride: previousItem.unitOverride,
            }
          : item,
      ),
    });
  });
}

/**
 * Remove (or restore) one grocery line -- the "I already own this" edit
 * (spec: docs/superpowers/specs/2026-08-26-grocery-list-editing-design.md).
 * Restore is the SAME mutation with `removed: false`; there is no second
 * endpoint and no destructive path.
 *
 * Optimistic because this is tapped one-handed in a store aisle and must
 * feel instant. Rolls back on failure so a dropped request never leaves a
 * line looking removed when the server still has it.
 *
 * Invalidates (rather than patches) the meal-plans cache: its embedded
 * grocery projection is `{ id, checked }` only -- it carries no `removed`
 * field, so there is nothing there to patch honestly. Unlike a checkbox tap
 * this is a rare action, so the refetch cost is not the concern it was for
 * useToggleGroceryItem.
 */
export function useRemoveGroceryItem(groceryListId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ itemId, removed }: { itemId: string; removed: boolean }) =>
      groceryListApi.removeGroceryItem(groceryListId, itemId, removed),
    onMutate: ({ itemId, removed }) =>
      beginOptimisticItemPatch(queryClient, itemId, { removed }),
    onError: (_error, _variables, context) =>
      rollbackGroceryLists(queryClient, context),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
  });
}

/**
 * Set an ABSOLUTE quantity on one grocery line -- the "buy this much" edit.
 * Writes `quantityOverride` and deliberately leaves the derived `quantity`
 * alone, so the UI can show the user their own number next to our
 * arithmetic (and so the server's fold logic can drop the override when the
 * derived quantity later changes). Same optimistic/rollback/invalidate
 * shape as useRemoveGroceryItem.
 */
export function useSetGroceryItemQuantity(groceryListId: string) {
  const queryClient = useQueryClient();
  // `unit` is present only when the line being edited is unquantified
  // (`item.unit === ""`, GroceryList.tsx's `isUnitless`) -- the unit picker
  // (#1023). It travels through the SAME mutation/outbox as `quantity`,
  // never a second request.
  type QuantityVariables = { itemId: string; quantity: number; unit?: string };
  type PreparedQuantityOperation = {
    checkoffEpoch: string;
    checkoffOperationId: string;
    baselineCheckoffOperationId: string | null;
  };
  const preparedOperations = useRef(
    new WeakMap<QuantityVariables, PreparedQuantityOperation>(),
  );
  const inFlightItems = useRef(new Set<string>());

  const prepareOperation = (
    variables: QuantityVariables,
  ): PreparedQuantityOperation => {
    const baseline = getConfirmedServerCheckoff(groceryListId, variables.itemId);
    if (!baseline) {
      throw new TerminalDirectCheckoffError(
        "That item needs a fresh server state. Refresh and try again.",
        "integrity",
      );
    }
    const storage = safeCheckoffStorage();
    const evidence = getConfirmedServerCheckoffEvidence(
      groceryListId,
      variables.itemId,
    );
    const markerRead = readPolicyRecoveryMarker(storage, variables.itemId);
    const recovering =
      markerRead.kind === "valid" ||
      (markerRead.kind === "absent" &&
        !!loadProcessPolicyRecoveryMarker(variables.itemId));
    const outbox = recovering
      ? loadOutboxSourcesWithHealth(storage)
      : { sources: [], health: { complete: true, malformed: false, unreadable: false } };
    const retirements = recovering
      ? loadCheckoffRetirementMaximaWithHealth(storage)
      : { maxima: [], health: { complete: true, malformed: false, unreadable: false } };
    const v3Retirements = recovering
      ? loadCheckoffRetirementV3MaximaWithHealth(storage)
      : { maxima: [], health: { complete: true, malformed: false, unreadable: false } };
    const observedEntries = recovering
      ? outbox.sources
          .flatMap((source) => source.entries)
          .filter(
            (entry) =>
              entry.groceryListId === groceryListId &&
              entry.itemId === variables.itemId,
          )
      : [];
    const observedRetirementOperationIds = recovering
      ? [...retirements.maxima, ...v3Retirements.maxima]
          .filter(
            (record) =>
              record.groceryListId === groceryListId &&
              record.itemId === variables.itemId,
          )
          .map((record) => record.supersededThroughOperationId)
      : [];
    const recoverySourceHealth = {
      complete:
        outbox.health.complete &&
        retirements.health.complete &&
        v3Retirements.health.complete,
      malformed:
        outbox.health.malformed ||
        retirements.health.malformed ||
        v3Retirements.health.malformed,
      unreadable:
        outbox.health.unreadable ||
        retirements.health.unreadable ||
        v3Retirements.health.unreadable,
    };
    const generated = createPolicySafeCheckoffOperationId({
      storage,
      target: { groceryListId, itemId: variables.itemId },
      baseline,
      baselineItemId: variables.itemId,
      authoritativeServerTimeMs: evidence?.serverTimeMs,
      observedEntries,
      observedRetirementOperationIds,
      ordinaryRetirementOperationIds: [],
      recoverySourcesComplete: recovering,
      recoverySourceHealth,
    });
    if (generated.kind === "blocked") {
      throw new TerminalDirectCheckoffError(
        "That item needs a fresh server state. Refresh and try again.",
        "integrity",
      );
    }
    return {
      checkoffEpoch: baseline.checkoffEpoch,
      checkoffOperationId: generated.operationId,
      baselineCheckoffOperationId: baseline.checkoffOperationId,
    };
  };

  return useMutation({
    networkMode: "always",
    retry: false,
    mutationFn: async (variables: QuantityVariables) => {
      const prepared =
        preparedOperations.current.get(variables) ?? prepareOperation(variables);
      preparedOperations.current.delete(variables);
      let result: GroceryItemCausalMutationResult;
      try {
        result = await groceryListApi.setGroceryItemQuantity(groceryListId, {
          itemId: variables.itemId,
          quantity: variables.quantity,
          ...(variables.unit !== undefined ? { unit: variables.unit } : {}),
          checkoffEpoch: prepared.checkoffEpoch,
          checkoffOperationId: prepared.checkoffOperationId,
        });
      } catch (error) {
        const causalError = parseCheckoffCausalTransportError(error);
        const status = errorStatus(error);
        if (status === 404) {
          purgeDeletedCheckoffTargetState(
            safeCheckoffStorage(),
            groceryListId,
            variables.itemId,
          );
          invalidateCheckoffQueries(queryClient);
          throw new TerminalDirectCheckoffError(
            "That grocery item is no longer available.",
            "integrity",
            status,
          );
        }
        if (causalError?.kind === "policy-rejected") {
          const details = causalError.details;
          if (
            details.oldEpoch !== prepared.checkoffEpoch ||
            details.rejectedOperationId !== prepared.checkoffOperationId
          ) {
            throw new TerminalDirectCheckoffError(
              "That item changed on another device. Refresh and try again.",
              "integrity",
              status,
              true,
            );
          }
          const confirmed = getConfirmedServerCheckoff(
            groceryListId,
            variables.itemId,
          );
          // A matching rejection has already admitted this operation as
          // poisoned, even when a newer GET later wins the visible boundary.
          resetCheckoffOperationProcessClock();
          if (
            !confirmed ||
            compareCheckoffEpochs(confirmed.checkoffEpoch, details.newEpoch) <= 0
          ) {
            purgeConfirmedCheckoffTargetState(
              safeCheckoffStorage(),
              groceryListId,
              variables.itemId,
              details.newEpoch,
            );
            // The onError reconciliation below is the sole confirmation
            // boundary. It merge-promotes this policy result and therefore
            // cannot overwrite a newer authoritative GET.
            savePolicyRecoveryMarker(safeCheckoffStorage(), {
              schemaVersion: 1,
              itemId: variables.itemId,
              serverTimeMs: details.serverTimeMs,
              oldEpoch: details.oldEpoch,
              newEpoch: details.newEpoch,
              rejectedOperationId: details.rejectedOperationId,
              policyCeiling: CHECKOFF_POLICY_CEILING,
            });
          }
          throw new ReconciledQuantityMutationError(
            "Your device clock is out of sync. Check it and try again.",
            "policy",
            status,
            {
              id: variables.itemId,
              checked: details.checked,
              checkoffEpoch: details.newEpoch,
              checkoffOperationId: null,
            },
            details.serverTimeMs,
          );
        }
        if (causalError?.kind === "required") {
          const details = causalError.details;
          throw new ReconciledQuantityMutationError(
            "That item needs a fresh server state. Refresh and try again.",
            "integrity",
            status,
            {
              id: variables.itemId,
              checked: details.checked,
              checkoffEpoch: details.acceptedEpoch,
              checkoffOperationId: details.acceptedOperationId,
            },
          );
        }
        if (status === 409 || status === 428) {
          throw new TerminalDirectCheckoffError(
            "That item changed on another device. Refresh and try again.",
            "integrity",
            status,
            true,
          );
        }
        throw error;
      }
      if (
        !isAuthoritativeCausalQuantityResult(result, {
          groceryListId,
          itemId: variables.itemId,
          quantity: variables.quantity,
          checkoffEpoch: prepared.checkoffEpoch,
          checkoffOperationId: prepared.checkoffOperationId,
        })
      ) {
        throw new TerminalDirectCheckoffError(
          "The server returned an invalid quantity result. Refresh and try again.",
          "integrity",
          undefined,
          true,
        );
      }
      if (
        result.causal.outcome === "stale" &&
        !hasConvergedCausalQuantity(result, variables.quantity)
      ) {
        throw new ReconciledQuantityMutationError(
          "That item changed on another device. Review the current quantity and try again.",
          "integrity",
          undefined,
          result,
          result.causal.serverTimeMs,
          result.quantityOverride,
        );
      }
      return result;
    },
    onMutate: async (variables: QuantityVariables) => {
      if (inFlightItems.current.has(variables.itemId)) {
        throw new DuplicateQuantityMutationError();
      }
      inFlightItems.current.add(variables.itemId);
      try {
        const prepared = prepareOperation(variables);
        preparedOperations.current.set(variables, prepared);
        const snapshot = await beginOptimisticItemPatch(queryClient, variables.itemId, {
          quantityOverride: variables.quantity,
          ...(variables.unit !== undefined ? { unitOverride: variables.unit } : {}),
        });
        return { ...snapshot, prepared };
      } catch (error) {
        preparedOperations.current.delete(variables);
        inFlightItems.current.delete(variables.itemId);
        throw error;
      }
    },
    onError: (error, variables, context) => {
      if (isDuplicateQuantityMutationError(error)) return;
      if (context) inFlightItems.current.delete(variables.itemId);
      if (isReconciledQuantityMutationError(error)) {
        if (context?.prepared) {
          rollbackQuantityOverrideAtPreparedBoundary(
            queryClient,
            groceryListId,
            variables.itemId,
            context.prepared,
            context,
          );
        }
        reconcileAuthoritativeMutationItem(
          queryClient,
          groceryListId,
          error.authoritativeItem,
          error.serverTimeMs,
        );
        if (
          !!context &&
          error.quantityOverride !== undefined &&
          retainsConfirmedCausalItem(groceryListId, error.authoritativeItem)
        ) {
          patchCachedGroceryItem(queryClient, variables.itemId, {
            quantityOverride: error.quantityOverride,
          });
        }
        invalidateCheckoffQueries(queryClient);
        return;
      }
      if (isTerminalDirectCheckoffError(error)) {
        if (error.restoreSnapshot && context?.prepared) {
          rollbackQuantityOverrideAtPreparedBoundary(
            queryClient,
            groceryListId,
            variables.itemId,
            context.prepared,
            context,
          );
        }
        invalidateCheckoffQueries(queryClient);
        return;
      }
      if (context?.prepared) {
        rollbackQuantityOverrideAtPreparedBoundary(
          queryClient,
          groceryListId,
          variables.itemId,
          context.prepared,
          context,
        );
      }
      invalidateCheckoffQueries(queryClient);
    },
    onSuccess: (result, variables, context) => {
      if (context) inFlightItems.current.delete(variables.itemId);
      reconcileAuthoritativeMutationItem(
        queryClient,
        groceryListId,
        result,
        result.causal.serverTimeMs,
      );
      if (
        context &&
        retainsConfirmedCausalItem(groceryListId, result)
      ) {
        patchCachedGroceryItem(queryClient, variables.itemId, {
          quantityOverride: result.quantityOverride,
          ...(variables.unit !== undefined
            ? { unitOverride: result.unitOverride ?? null }
            : {}),
        });
      }
      invalidateCheckoffQueries(queryClient);
    },
  });
}

type ResetBaseline = {
  checked: boolean;
  checkoffEpoch: string;
  checkoffOperationId: string | null;
};

type PreparedClearCheckedReset = {
  mealPlanId: string;
  listId: string;
  checkoffOperationId: string;
  epochsByItemId: Record<string, string>;
  baselinesByItemId: Record<string, ResetBaseline>;
  preRefreshRowsByStableKey: Record<string, {
    itemId: string;
    baseline: ResetBaseline;
    effectiveQuantity: number;
  }>;
  itemIds: readonly string[];
  recoveredMarkers: readonly CheckoffPolicyRecoveryMarkerV1[];
  authoritativeServerTimeMs: number;
};

function stableGroceryItemKey(
  item: Pick<GroceryItem, "ingredientId" | "unit">,
): string {
  return `${item.ingredientId}\u0000${item.unit}`;
}

function effectiveGroceryItemQuantity(
  item: Pick<GroceryItem, "quantity" | "quantityOverride">,
): number {
  return item.quantityOverride ?? item.quantity;
}

function resetSnapshotIdentity(
  snapshot: ReturnType<typeof decodeAuthoritativeGroceryCheckoffSnapshot>,
): string {
  if (!snapshot) return "";
  return JSON.stringify(
    snapshot.items
      .map((item) => [
        item.id,
        item.checked,
        item.checkoffEpoch,
        item.checkoffOperationId,
      ])
      .sort((left, right) => String(left[0]).localeCompare(String(right[0]))),
  );
}

function resetListIdentityIsCanonical(
  queryClient: QueryClient,
  mealPlanId: string,
  listId: string,
): boolean {
  const nestedListIds = new Set<string>();
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [MEAL_PLANS_QUERY_KEY] })
    .forEach((query) => {
      const value = query.state.data;
      const plans: MealPlan[] = Array.isArray(value)
        ? value
        : value && typeof value === "object"
          ? [value as MealPlan]
          : [];
      plans
        .filter((plan) => plan.id === mealPlanId)
        .forEach((plan) => {
          if (plan.groceryList?.id) nestedListIds.add(plan.groceryList.id);
        });
    });
  return nestedListIds.size === 0 || nestedListIds.has(listId);
}

function prepareClearCheckedReset(
  queryClient: QueryClient,
  mealPlanId: string,
): PreparedClearCheckedReset | undefined {
  const candidates = queryClient
    .getQueryCache()
    .findAll({ queryKey: [GROCERY_LISTS_QUERY_KEY] })
    .map((query) => query.state.data)
    .filter(
      (value): value is GroceryList =>
        isGroceryList(value) && value.mealPlanId === mealPlanId,
    )
    .map((list) => {
      const snapshot = decodeAuthoritativeGroceryCheckoffSnapshot(list);
      if (!snapshot || !resetListIdentityIsCanonical(queryClient, mealPlanId, list.id)) {
        return undefined;
      }
      const ids = snapshot.items.map((item) => item.id);
      if (new Set(ids).size !== ids.length) return undefined;
      const evidence = snapshot.items.map((item) =>
        getConfirmedServerCheckoffEvidence(snapshot.id, item.id),
      );
      if (
        evidence.some(
          (entry, index) =>
            !entry ||
            !isValidServerTimeMs(entry.serverTimeMs) ||
            entry.baseline.checked !== snapshot.items[index]!.checked ||
            entry.baseline.checkoffEpoch !== snapshot.items[index]!.checkoffEpoch ||
            entry.baseline.checkoffOperationId !==
              snapshot.items[index]!.checkoffOperationId,
        )
      ) {
        return undefined;
      }
      return { list, snapshot, evidence };
    })
    .filter(
      (value): value is {
        list: GroceryList;
        snapshot: NonNullable<ReturnType<typeof decodeAuthoritativeGroceryCheckoffSnapshot>>;
        evidence: Array<NonNullable<ReturnType<typeof getConfirmedServerCheckoffEvidence>>>;
      } => !!value,
    );

  if (candidates.length === 0) return undefined;
  const greatestTime = Math.max(...candidates.map(({ snapshot }) => snapshot.serverTimeMs));
  const newest = candidates.filter(({ snapshot }) => snapshot.serverTimeMs === greatestTime);
  const identities = new Set(newest.map(({ snapshot }) => resetSnapshotIdentity(snapshot)));
  const listIds = new Set(newest.map(({ snapshot }) => snapshot.id));
  if (identities.size !== 1 || listIds.size !== 1) return undefined;
  const selected = newest[0]!;
  const preRefreshRowsByStableKey: PreparedClearCheckedReset["preRefreshRowsByStableKey"] = {};
  for (const item of selected.list.items) {
    const snapshotItem = selected.snapshot.items.find((candidate) => candidate.id === item.id);
    if (
      !snapshotItem ||
      typeof item.ingredientId !== "string" ||
      typeof item.unit !== "string" ||
      typeof item.quantity !== "number" ||
      (item.quantityOverride !== undefined &&
        item.quantityOverride !== null &&
        typeof item.quantityOverride !== "number")
    ) return undefined;
    const stableKey = stableGroceryItemKey(item);
    if (Object.prototype.hasOwnProperty.call(preRefreshRowsByStableKey, stableKey)) {
      return undefined;
    }
    preRefreshRowsByStableKey[stableKey] = {
      itemId: item.id,
      baseline: {
        checked: snapshotItem.checked,
        checkoffEpoch: snapshotItem.checkoffEpoch,
        checkoffOperationId: snapshotItem.checkoffOperationId,
      },
      effectiveQuantity: effectiveGroceryItemQuantity(item),
    };
  }
  const storage = safeCheckoffStorage();
  const targets = selected.snapshot.items.map((item) => ({
    target: { groceryListId: selected.snapshot.id, itemId: item.id },
    baseline: {
      checked: item.checked,
      checkoffEpoch: item.checkoffEpoch,
      checkoffOperationId: item.checkoffOperationId,
    },
    baselineItemId: item.id,
  }));
  const markerReads = selected.snapshot.items.map((item) => ({
    itemId: item.id,
    durable: readPolicyRecoveryMarker(storage, item.id),
    process: loadProcessPolicyRecoveryMarker(item.id),
  }));
  if (markerReads.some(({ durable }) => durable.kind === "invalid" || durable.kind === "unreadable")) {
    return undefined;
  }
  const recovering = markerReads.some(({ durable, process }) => durable.kind === "valid" || !!process);
  const outbox = recovering
    ? loadOutboxSourcesWithHealth(storage)
    : { sources: [], health: { complete: true, malformed: false, unreadable: false } };
  const retirements = recovering
    ? loadCheckoffRetirementMaximaWithHealth(storage)
    : { maxima: [], health: { complete: true, malformed: false, unreadable: false } };
  const v3Retirements = recovering
    ? loadCheckoffRetirementV3MaximaWithHealth(storage)
    : { maxima: [], health: { complete: true, malformed: false, unreadable: false } };
  const targetIds = new Set(selected.snapshot.items.map((item) => item.id));
  const observedEntries = recovering
    ? outbox.sources.flatMap((source) => source.entries).filter(
        (entry) => entry.groceryListId === selected.snapshot.id && targetIds.has(entry.itemId),
      )
    : [];
  const observedRetirementOperationIds = recovering
    ? [...retirements.maxima, ...v3Retirements.maxima]
        .filter((record) => record.groceryListId === selected.snapshot.id && targetIds.has(record.itemId))
        .map((record) => record.supersededThroughOperationId)
    : [];
  const recoverySourceHealth = {
    complete: outbox.health.complete && retirements.health.complete && v3Retirements.health.complete,
    malformed: outbox.health.malformed || retirements.health.malformed || v3Retirements.health.malformed,
    unreadable: outbox.health.unreadable || retirements.health.unreadable || v3Retirements.health.unreadable,
  };
  const generated = createPolicySafeCheckoffOperationIdForTargets({
    storage,
    targets,
    authoritativeServerTimeMs: selected.snapshot.serverTimeMs,
    observedEntries,
    observedRetirementOperationIds,
    recoverySourcesComplete: recovering,
    recoverySourceHealth,
  });
  if (generated.kind !== "created") return undefined;
  return {
    mealPlanId,
    listId: selected.snapshot.id,
    checkoffOperationId: generated.operationId,
    epochsByItemId: Object.fromEntries(selected.snapshot.items.map((item) => [item.id, item.checkoffEpoch])),
    baselinesByItemId: Object.fromEntries(selected.snapshot.items.map((item) => [item.id, {
      checked: item.checked,
      checkoffEpoch: item.checkoffEpoch,
      checkoffOperationId: item.checkoffOperationId,
    }])),
    preRefreshRowsByStableKey,
    itemIds: selected.snapshot.items.map((item) => item.id),
    recoveredMarkers: generated.recoveredMarkers,
    authoritativeServerTimeMs: selected.snapshot.serverTimeMs,
  };
}

function clearRecoveredResetMarkers(
  markers: readonly CheckoffPolicyRecoveryMarkerV1[],
): void {
  const storage = safeCheckoffStorage();
  markers.forEach((marker) => {
    clearExactPolicyRecoveryMarker(storage, marker);
    clearExactProcessPolicyRecoveryMarker(marker);
  });
}

function resetPolicySummaryMatchesContext(
  summary: ClearCheckedPolicyRejectionSummary,
  context: PreparedClearCheckedReset,
): boolean {
  if (
    summary.requestedOperationId !== context.checkoffOperationId ||
    !isValidServerTimeMs(summary.serverTimeMs) ||
    summary.rows.length !== context.itemIds.length
  ) return false;
  const expected = new Set(context.itemIds);
  const seen = new Set<string>();
  return summary.rows.every((row) => {
    if (!expected.has(row.itemId) || seen.has(row.itemId)) return false;
    seen.add(row.itemId);
    const baseline = context.baselinesByItemId[row.itemId];
    if (!baseline || !isServerConfirmedCheckoff({
      checked: row.checked,
      checkoffEpoch: row.acceptedEpoch,
      checkoffOperationId: row.acceptedOperationId,
    })) return false;
    if (compareCheckoffEpochs(row.acceptedEpoch, baseline.checkoffEpoch) < 0) return false;
    if (row.outcome === "revoked-new-intent-required") {
      return (
        row.checked === baseline.checked &&
        row.acceptedOperationId === null &&
        row.acceptedEpoch === incrementCanonicalDecimal(baseline.checkoffEpoch)
      );
    }
    if (row.outcome === "idempotent") {
      return (
        row.acceptedEpoch === baseline.checkoffEpoch &&
        row.acceptedOperationId === context.checkoffOperationId &&
        row.checked === false
      );
    }
    return compareCausalItems(
      {
        checkoffEpoch: row.acceptedEpoch,
        checkoffOperationId: row.acceptedOperationId,
      },
      baseline,
    ) > 0;
  }) && seen.size === expected.size;
}

function resetSuccessResponseMatchesContext(
  incoming: unknown,
  context: PreparedClearCheckedReset,
): incoming is ResetGroceryListResponse {
  if (!isGroceryList(incoming) || incoming.mealPlanId !== context.mealPlanId) {
    return false;
  }
  const snapshot = decodeAuthoritativeGroceryCheckoffSnapshot(incoming);
  if (!snapshot || snapshot.id !== context.listId) return false;
  const returnedByStableKey = new Map<string, (typeof snapshot.items)[number]>();
  const returnedItemsById = new Map<string, GroceryItem>();
  for (const item of (incoming as GroceryList).items) {
    if (
      typeof item.id !== "string" ||
      typeof item.ingredientId !== "string" ||
      typeof item.unit !== "string" ||
      typeof item.quantity !== "number" ||
      (item.quantityOverride !== undefined &&
        item.quantityOverride !== null &&
        typeof item.quantityOverride !== "number")
    ) return false;
    const stableKey = stableGroceryItemKey(item);
    if (returnedByStableKey.has(stableKey) || returnedItemsById.has(item.id)) return false;
    const snapshotItem = snapshot.items.find((candidate) => candidate.id === item.id);
    if (!snapshotItem) return false;
    returnedByStableKey.set(stableKey, snapshotItem);
    returnedItemsById.set(item.id, item);
  }
  const causal = (incoming as { causal?: unknown }).causal;
  if (!causal || typeof causal !== "object") return false;
  const summary = causal as {
    requestedOperationId?: unknown;
    rows?: unknown;
  };
  if (
    summary.requestedOperationId !== context.checkoffOperationId ||
    !Array.isArray(summary.rows) ||
    summary.rows.length !== [...returnedByStableKey.keys()].filter((key) =>
      Object.prototype.hasOwnProperty.call(context.preRefreshRowsByStableKey, key),
    ).length
  ) return false;
  const survivingPreKeys = new Set(
    [...returnedByStableKey.keys()].filter((key) =>
      Object.prototype.hasOwnProperty.call(context.preRefreshRowsByStableKey, key),
    ),
  );
  for (const [stableKey, item] of returnedByStableKey) {
    if (
      !Object.prototype.hasOwnProperty.call(context.preRefreshRowsByStableKey, stableKey) &&
      (item.checked !== false || item.checkoffEpoch !== "0" || item.checkoffOperationId !== null)
    ) return false;
  }
  const seen = new Set<string>();
  return summary.rows.every((value) => {
    if (!value || typeof value !== "object") return false;
    const row = value as Record<string, unknown>;
    if (
      typeof row.itemId !== "string" ||
      seen.has(row.itemId) ||
      typeof row.checked !== "boolean" ||
      typeof row.acceptedEpoch !== "string" ||
      (row.acceptedOperationId !== null &&
        typeof row.acceptedOperationId !== "string") ||
      typeof row.outcome !== "string"
    ) return false;
    const returnedItem = returnedItemsById.get(row.itemId);
    const item = snapshot.items.find((candidate) => candidate.id === row.itemId);
    if (!returnedItem || !item || item.checked !== row.checked || item.checkoffEpoch !== row.acceptedEpoch || item.checkoffOperationId !== row.acceptedOperationId) {
      return false;
    }
    const stableKey = stableGroceryItemKey(returnedItem);
    const pre = context.preRefreshRowsByStableKey[stableKey];
    if (!pre || !survivingPreKeys.has(stableKey)) return false;
    if (!isServerConfirmedCheckoff({
      checked: row.checked,
      checkoffEpoch: row.acceptedEpoch,
      checkoffOperationId: row.acceptedOperationId,
    })) return false;
    const quantityFolded =
      effectiveGroceryItemQuantity(returnedItem) !== pre.effectiveQuantity;
    const foldedEpoch = incrementCanonicalDecimal(pre.baseline.checkoffEpoch);
    if (row.outcome === "legacy-applied") return false;
    if (row.outcome === "applied") {
      if (row.checked !== false || row.acceptedOperationId !== context.checkoffOperationId) return false;
      const expectedEpoch = quantityFolded
        ? foldedEpoch
        : pre.baseline.checkoffEpoch;
      if (row.acceptedEpoch !== expectedEpoch) return false;
    } else if (row.outcome === "retained-newer") {
      if (
        compareCausalItems(
          {
            checkoffEpoch: row.acceptedEpoch,
            checkoffOperationId: row.acceptedOperationId,
          },
          {
            checkoffEpoch: pre.baseline.checkoffEpoch,
            checkoffOperationId: context.checkoffOperationId,
          },
        ) <= 0
      ) return false;
    } else if (row.outcome === "idempotent") {
      if (row.checked !== false) return false;
      const exact = !quantityFolded && row.acceptedEpoch === pre.baseline.checkoffEpoch && row.acceptedOperationId === context.checkoffOperationId;
      const folded = quantityFolded && row.acceptedEpoch === foldedEpoch && row.acceptedOperationId === null;
      if (!exact && !folded) return false;
    } else {
      return false;
    }
    seen.add(row.itemId);
    return true;
  }) && seen.size === summary.rows.length;
}

async function invalidateResetQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] }),
  ]);
}

async function recoverUncertainReset(
  queryClient: QueryClient,
  mealPlanId: string,
  prepared: PreparedClearCheckedReset,
  status: number | undefined,
): Promise<never> {
  try {
    const incoming = await groceryListApi.getGroceryListByMealPlan(mealPlanId, "aisle");
    const snapshot = decodeAuthoritativeGroceryCheckoffSnapshot(incoming);
    if (
      !snapshot ||
      snapshot.id !== prepared.listId ||
      incoming.mealPlanId !== mealPlanId
    ) throw new Error("incomplete reset GET");
    const targetKey = [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlanId, "aisle"] as const;
    const reconciled = reconcileAuthoritativeGroceryList(queryClient, incoming, targetKey);
    queryClient.setQueryData(targetKey, reconciled);
  } catch {
    // The guarded GET is evidence collection only; it never replays the POST.
  }
  await invalidateResetQueries(queryClient);
  throw new TerminalDirectCheckoffError(
    "The grocery reset outcome is uncertain. Refresh before trying again.",
    "integrity",
    status,
  );
}

/**
 * Rebuild a meal plan's grocery list from its current slots (Track C,
 * Task 5, issue #448) -- the "Rebuild list" control's mutation. Invalidates
 * the grocery-list query (both groupings share the underlying items) and
 * the meal-plans list's embedded checked-count projection, same as
 * useAddGroceryItem.
 */
export function useResetGroceryList(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: async (clearChecked?: boolean) => {
      if (!clearChecked) {
        return groceryListApi.resetGroceryList(mealPlanId, false);
      }
      const prepared = prepareClearCheckedReset(queryClient, mealPlanId);
      if (!prepared) {
        throw new TerminalDirectCheckoffError(
          "That list needs a fresh server state. Refresh and try again.",
          "integrity",
        );
      }
      let incoming: GroceryList | ResetGroceryListResponse;
      try {
        incoming = await groceryListApi.resetGroceryList(mealPlanId, {
          clearChecked: true,
          checkoffOperationId: prepared.checkoffOperationId,
          epochsByItemId: prepared.epochsByItemId,
        });
      } catch (error) {
        const causalError = parseCheckoffCausalTransportError(error);
        const status = errorStatus(error);
        if (causalError?.kind === "reset-policy-rejected") {
          if (!resetPolicySummaryMatchesContext(causalError.details, prepared)) {
            await invalidateResetQueries(queryClient);
            throw new TerminalDirectCheckoffError(
              "The grocery reset response was invalid. Refresh and try again.",
              "integrity",
              status,
            );
          }
          const storage = safeCheckoffStorage();
          causalError.details.rows.forEach((row) => {
            purgeConfirmedCheckoffTargetState(
              storage,
              prepared.listId,
              row.itemId,
              row.acceptedEpoch,
            );
          });
          resetCheckoffOperationProcessClock();
          causalError.details.rows
            .filter((row) => row.outcome === "revoked-new-intent-required")
            .forEach((row) => {
              const baseline = prepared.baselinesByItemId[row.itemId];
              if (!baseline) return;
              savePolicyRecoveryMarker(storage, {
                schemaVersion: 1,
                itemId: row.itemId,
                serverTimeMs: causalError.details.serverTimeMs,
                oldEpoch: baseline.checkoffEpoch,
                newEpoch: row.acceptedEpoch,
                rejectedOperationId: prepared.checkoffOperationId,
                policyCeiling: CHECKOFF_POLICY_CEILING,
              });
            });
          causalError.details.rows.forEach((row) => {
            reconcileAuthoritativeCausalState(
              queryClient,
              prepared.listId,
              row.itemId,
              row.checked,
              row.acceptedEpoch,
              row.acceptedOperationId,
              causalError.details.serverTimeMs,
            );
          });
          await invalidateResetQueries(queryClient);
          throw new TerminalDirectCheckoffError(
            "Your device clock is out of sync. Check it and try again.",
            "policy",
            status,
          );
        }
        if (status === 409 || status === 428) {
          await invalidateResetQueries(queryClient);
          throw new TerminalDirectCheckoffError(
            "That grocery list changed on another device. Refresh and try again.",
            "integrity",
            status,
          );
        }
        if (isRetryableCheckoffError(error)) {
          return recoverUncertainReset(queryClient, mealPlanId, prepared, status);
        }
        await invalidateResetQueries(queryClient);
        throw new TerminalDirectCheckoffError(
          "The grocery reset failed. Refresh and try again.",
          "integrity",
          status,
        );
      }
      if (!resetSuccessResponseMatchesContext(incoming, prepared)) {
        await invalidateResetQueries(queryClient);
        throw new TerminalDirectCheckoffError(
          "The grocery reset response was invalid. Refresh and try again.",
          "integrity",
        );
      }
      const targetKey = [
        GROCERY_LISTS_QUERY_KEY,
        "meal-plan",
        mealPlanId,
        "aisle",
      ] as const;
      const reconciled = reconcileAuthoritativeGroceryList(
        queryClient,
        incoming,
        targetKey,
      );
      queryClient.setQueryData(targetKey, reconciled);
      clearRecoveredResetMarkers(prepared.recoveredMarkers);
      await invalidateResetQueries(queryClient);
      return incoming;
    },
    onSuccess: async (incoming, clearChecked) => {
      if (clearChecked) return;
      const targetKey = [
        GROCERY_LISTS_QUERY_KEY,
        "meal-plan",
        mealPlanId,
        "aisle",
      ] as const;
      const reconciled = reconcileAuthoritativeGroceryList(
        queryClient,
        incoming,
        targetKey,
      );
      queryClient.setQueryData(targetKey, reconciled);
      await invalidateResetQueries(queryClient);
    },
  });
}

/** Empty a meal plan's grocery list without deleting the list itself. */
export function useClearGroceryList(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => groceryListApi.clearGroceryList(mealPlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROCERY_LISTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
  });
}

/**
 * Export grocery list
 */
export function useExportGroceryList() {
  return useMutation({
    mutationFn: (id: string) => groceryListApi.exportGroceryList(id),
  });
}

/**
 * Query key builder for the "Trim the bill" suggestions query (Track D, D3,
 * issue #384) -- exported so TrimTheBillCard can invalidate it directly
 * after a successful swap (useSwapMeal only invalidates the
 * meal-plan-keyed grocery list query, which is a DIFFERENT cache entry from
 * this groceryListId-keyed one).
 */
export function savingsSuggestionsQueryKey(groceryListId: string) {
  return [GROCERY_LISTS_QUERY_KEY, groceryListId, "savings-suggestions"];
}

/**
 * "Trim the bill" swap suggestions for a grocery list (Track D, D3, issue
 * #384).
 */
export function useSavingsSuggestions(groceryListId: string) {
  return useQuery({
    queryKey: savingsSuggestionsQueryKey(groceryListId),
    queryFn: () => groceryListApi.getSavingsSuggestions(groceryListId),
    enabled: !!groceryListId,
  });
}

/**
 * "Keep it" -- durably dismisses one trim-the-bill suggestion (Track D, D3,
 * issue #384). Invalidates the suggestions query so the dismissed pair
 * disappears and the card advances to the next qualifying suggestion (or
 * unmounts if there isn't one).
 */
export function useDismissSavingsSuggestion(groceryListId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DismissSavingsSuggestionRequest) =>
      groceryListApi.dismissSavingsSuggestion(groceryListId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: savingsSuggestionsQueryKey(groceryListId),
      });
    },
  });
}
